import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import JSZip from 'jszip';
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { NOTE_CATEGORIES, DEFAULT_NOTE_CATEGORY, getNoteCategoryBadgeClasses } from "../../lib/common/noteCategories";
import {
    getSingleAssignment,
    addJobNote,
    updateJobNote,
    deleteJobNote,
    setImportantJobNote,
    getAvailableDesigners,
    getAvailableEstimators,
    getAvailableDrivers,
    assignDesigner,
    removeDesigner,
    assignEstimator,
    removeEstimator,
    assignDriver,
    removeDriver,
    confirmPackageReceipt,
    uploadDeliveryPhoto,
    completeDelivery,
    completeDesign,
    completeEstimation,
    subscribeToJobUpdates,
    toggleQuoteSent,
    createNewDelivery,
    deleteJob,
    removeSelfAssignment,
    uploadJobFile
} from "../../lib/supabase/single-assignment";

import {
    FiArrowLeft, FiEdit3, FiDownload, FiCalendar, FiUser, FiPhone,
    FiMapPin, FiCheckCircle, FiClock, FiAlertCircle, FiFileText,
    FiHome, FiLayers, FiTool, FiDollarSign, FiFlag, FiUpload,
    FiCheck, FiX, FiEye, FiMail, FiTruck,
    FiPenTool, FiMap, FiSend, FiTrash2,
    FiDelete, FiPackage,
    FiFolder,
    FiFolderMinus,
    FiChevronDown,
    FiPlus,
    FiFolderPlus,
    FiMessageSquare
} from "react-icons/fi";

import { GiWoodBeam } from "react-icons/gi";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";

import { FiImage, FiFile, FiFilePdf } from "react-icons/fi";
import { Home, BrickWall, Code, FileArchive, FileAudio, FileSpreadsheet, FileVideo, Grid2X2, Loader, PanelsTopLeft, Calendar, PartyPopper, PauseCircle, SquareStack, PencilRuler, Receipt, Users, Info, NotebookPen } from "lucide-react";
import { FaRegFileWord, FaRegFilePdf } from "react-icons/fa6";

import DragDropUploader from "../common/DragDropUploader";
import FileViewer from '../common/FileViewer';
import { checkPermissions, hasPermission } from "../../lib/permissions";
import { sendInTransitNotificationSMS, sendDeliveredNotificationSMS, sendDriverSMS } from "../../lib/supabase/sms";
import { createNotification, NOTIFICATION_TYPES } from "../../lib/supabase/notifications";
import { BackButtonIconOnly } from "../common/BackButton";
import Toast from "../common/Toast";
import { getJobStatusSummary, getJobStatusSummarySingleQuote } from "../../lib/common/jobStatusText";
import DropdownSection from "../common/DropdownSection";
import { BsExclamationTriangle } from "react-icons/bs";
import TextTooltip from "../common/TextTooltip";
import ActivityLogsTable from "./ActivityLogsTable";
import PricingReviewDropdown from "./PricingReviewDropdown";
import { getInvoiceSourceIcon, getInvoiceStatusSource, getInvoiceStatusText } from "../../lib/common/invoiceDisplay";
import { QUOTE_ORDER, getQuoteLabel } from "../../lib/quoteTypes";
import XeroInvoicePdfModal from "../modals/XeroInvoicePdfModal";
import JobBookedDatesEmailSender from "../common/JobBookedDatesEmailSender";
import { useHolidays } from "../../hooks/useHolidays";

// Defined at module scope (not inside SingleDepartment) so typing here only
// re-renders this small form, not the entire page tree.
const NoteSubmitButton = ({ onClick, disabled, className, children }) => (
    disabled ? (
        <TextTooltip text="This job is cancelled">
            <span className="inline-flex cursor-not-allowed">
                <button disabled className={`${className} opacity-50 cursor-not-allowed pointer-events-none`}>
                    {children}
                </button>
            </span>
        </TextTooltip>
    ) : (
        <button onClick={onClick} className={className}>
            {children}
        </button>
    )
);

const AddNoteForm = ({ onAdd, isJobCancelled }) => {
    const [content, setContent] = useState("");
    const [category, setCategory] = useState(DEFAULT_NOTE_CATEGORY);

    const handleAdd = () => {
        if (!content.trim()) return;
        onAdd(content.trim(), category);
        setContent("");
        setCategory(DEFAULT_NOTE_CATEGORY);
    };

    return (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">Add New Note</label>
                <select
                    className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                >
                    {NOTE_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
                <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Add a note to this assignment..."
                    rows="3"
                />
                {content.trim() && (
                    <NoteSubmitButton
                        onClick={handleAdd}
                        disabled={isJobCancelled}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                    >
                        Add Note
                    </NoteSubmitButton>
                )}
            </div>
        </div>
    );
};

const EditNoteForm = ({ note, onSave, onCancel, isJobCancelled }) => {
    const [content, setContent] = useState(note.content);
    const [category, setCategory] = useState(note.category || DEFAULT_NOTE_CATEGORY);

    return (
        <div className="space-y-3">
            <select
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
            >
                {NOTE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                ))}
            </select>
            <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows="3"
            />
            <div className="flex gap-2">
                <NoteSubmitButton
                    onClick={() => onSave(note.id, content, category)}
                    disabled={isJobCancelled}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                >
                    Save Changes
                </NoteSubmitButton>
                <NoteSubmitButton
                    onClick={onCancel}
                    disabled={isJobCancelled}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                >
                    Cancel
                </NoteSubmitButton>
            </div>
        </div>
    );
};

const SingleDepartment = () => {
    const { userProfile, company } = useAuth();
    const holidayMap = useHolidays(company?.state);
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug, jobId, departmentId } = useParams();

    const [isScrolled, setIsScrolled] = useState(false);
    const [assignment, setAssignment] = useState(null);
    const isJobCancelled = !!assignment?.is_cancelled;
    const [department, setDepartment] = useState(null);
    const [xeroPdfModal, setXeroPdfModal] = useState({
        open: false, loading: false, error: null, pdfUrl: null, title: '',
    });

    const [actionLogs, setActionLogs] = useState([]);
    const [showLogs, setShowLogs] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);

    const [showDesignerModal, setShowDesignerModal] = useState(false);
    const [showEstimatorModal, setShowEstimatorModal] = useState(false);
    const [selectedQuoteType, setSelectedQuoteType] = useState(null);
    const [selectedChildQuoteId, setSelectedChildQuoteId] = useState(null);
    const [selectedChildQuoteName, setSelectedChildQuoteName] = useState(null);
    const [selectedDesignerId, setSelectedDesignerId] = useState('');
    const [selectedEstimatorId, setSelectedEstimatorId] = useState('');
    const [availableDesigners, setAvailableDesigners] = useState([]);
    const [availableEstimators, setAvailableEstimators] = useState([]);

    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isSubmittingCompletion, setIsSubmittingCompletion] = useState(false);
    const [clearPdfs, setClearPdfs] = useState(false);
    const [completionNotes, setCompletionNotes] = useState({
        roof: '',
        wall: '',
        floor: '',
        estimation: ''
    });
    const [quoteDeliveryNotes, setQuoteDeliveryNotes] = useState({
        roof: '',
        wall: '',
        floor: '',
        estimation: ''
    });

    // Driver delivery states - keyed by "quoteType-deliveryCount"
    const [uploadedDeliveryPhotos, setUploadedDeliveryPhotos] = useState({});
    const [clearDeliveryPhotos, setClearDeliveryPhotos] = useState({});
    const [deliveryNotes, setDeliveryNotes] = useState({});
    const [isSubmittingDelivery, setIsSubmittingDelivery] = useState(false);

    const [showDriverModal, setShowDriverModal] = useState(false);
    const [selectedQuoteTypeForDriver, setSelectedQuoteTypeForDriver] = useState(null);
    const [selectedDeliveryCount, setSelectedDeliveryCount] = useState(0);
    const [selectedChildQuoteIdForDriver, setSelectedChildQuoteIdForDriver] = useState(null);
    const [selectedChildQuoteNameForDriver, setSelectedChildQuoteNameForDriver] = useState(null);
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [availableDrivers, setAvailableDrivers] = useState([]);

    // File viewer states
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [viewerFiles, setViewerFiles] = useState([]);
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);

    // Quote sent state
    const [isTogglingQuoteSent, setIsTogglingQuoteSent] = useState(false);

    // Delete Job Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeletingJob, setIsDeletingJob] = useState(false);
    const isNavigatingAway = useRef(false);

    // New Delivery Modal states
    const [showNewDeliveryModal, setShowNewDeliveryModal] = useState(false);
    const [newDeliveryQuoteType, setNewDeliveryQuoteType] = useState(null);
    const [newDeliveryNote, setNewDeliveryNote] = useState('');
    const [newDeliveryTimeSlot, setNewDeliveryTimeSlot] = useState(null);
    const [newDeliveryScheduledDate, setNewDeliveryScheduledDate] = useState('');
    const [newDeliverySelectedUnits, setNewDeliverySelectedUnits] = useState([]);

    const [openFolders, setOpenFolders] = useState({});
    const [openQuoteFolders, setOpenQuoteFolders] = useState({});
    const [isDownloading, setIsDownloading] = useState(false);

    const [showFolderInput, setShowFolderInput] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [folderUploading, setFolderUploading] = useState(null); // upload key while uploading
    const [dragOverFolder, setDragOverFolder] = useState(null);
    const fileUploadInputRef = useRef(null);
    const uploadContext = useRef({ fileType: null, quoteType: null, folderName: null, isCustom: false });

    const isTouchDevice = 'ontouchstart' in window;

    const fetchAssignment = async () => {
        // Don't fetch if we're in the process of deleting or navigating away
        if (isDeletingJob || isNavigatingAway.current) return;

        try {
            const data = await getSingleAssignment(jobId, company.id);
            setAssignment(data);

            let foundDepartment = data?.job_quotes.find(q => q?.id === departmentId)
            setDepartment(foundDepartment)

            const sortedLogs = (data.job_action_logs || []).sort((a, b) =>
                new Date(b.timestamp) - new Date(a.timestamp)
            );
            setActionLogs(sortedLogs);
        } catch (error) {
            console.error("Error fetching assignment:", error);

            // Only show error and redirect if not currently deleting or navigating
            if (!isDeletingJob && !isNavigatingAway.current) {
                Toast.error("Assignment not found or has been deleted", {
                    title: 'Error',
                    position: 'bottom-right',
                    autoClose: 3000
                });
                navigate(`/${companySlug}/assignment/list`);
            }
        }
    };

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 10);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        if (company?.id && jobId && !isDeletingJob) {
            fetchAssignment();
        }
    }, [company, jobId]);

    // Re-resolve department when URL departmentId changes (navigating between child pills)
    useEffect(() => {
        if (assignment && departmentId) {
            const foundDepartment = assignment.job_quotes?.find(q => q?.id === departmentId);
            setDepartment(foundDepartment);
            // window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [departmentId, assignment]);

    // Real-time subscription
    useEffect(() => {
        if (!assignment?.id) return;

        const channel = subscribeToJobUpdates(assignment.id, (payload) => {
            fetchAssignment();
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [assignment?.id]);

    // Keyboard navigation for file viewer
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!fileViewerOpen) return;

            if (e.key === 'Escape') {
                setFileViewerOpen(false);
            } else if (e.key === 'ArrowLeft' && selectedFileIndex > 0) {
                setSelectedFileIndex(prev => prev - 1);
                setSelectedFile(viewerFiles[selectedFileIndex - 1]);
            } else if (e.key === 'ArrowRight' && selectedFileIndex < viewerFiles.length - 1) {
                setSelectedFileIndex(prev => prev + 1);
                setSelectedFile(viewerFiles[selectedFileIndex + 1]);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [fileViewerOpen, selectedFileIndex, viewerFiles]);

    const canUserModifyNote = (note) => {
        return note.added_by_user_id === userProfile?.id || userProfile?.role === 'admin';
    };

    const handleAddNote = async (content, category) => {
        if (!content?.trim()) return;

        try {
            await addJobNote(
                assignment.id,
                content.trim(),
                userProfile.id,
                userProfile.username || userProfile.full_name,
                userProfile.role,
                department,
                category
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error adding note:", error);
            alert("Error adding note");
        }
    };

    const startEditNote = (noteId) => {
        setEditingNoteId(noteId);
    };

    const cancelEditNote = () => {
        setEditingNoteId(null);
    };

    const handleSaveEditNote = async (noteId, content, category) => {
        if (!content?.trim()) return;

        try {
            await updateJobNote(noteId, content.trim(), userProfile.id, category);
            cancelEditNote();
            fetchAssignment();
        } catch (error) {
            console.error("Error updating note:", error);
            alert(error.message || "Error updating note");
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm("Are you sure you want to delete this note? This action cannot be undone.")) {
            return;
        }

        try {
            await deleteJobNote(noteId, userProfile.id, userProfile.role);
            fetchAssignment();
        } catch (error) {
            console.error("Error deleting note:", error);
            alert(error.message || "Error deleting note");
        }
    };

    const handleSetImportant = async (noteId, currentIsImportant) => {
        try {
            await setImportantJobNote(noteId, !currentIsImportant);
            fetchAssignment();
        } catch (error) {
            console.error("Error updating note importance:", error);
            alert(error.message || "Error updating note importance");
        }
    };

    const handleDeleteJob = async () => {
        if (deleteConfirmText !== assignment.unique_id) {
            return;
        }

        setIsDeletingJob(true);
        isNavigatingAway.current = true;

        try {
            await deleteJob(assignment.id, company.id, userProfile);

            // Close modal
            setShowDeleteModal(false);
            setDeleteConfirmText('');

            Toast.success('Job deleted successfully', {
                title: 'Deleted',
                position: 'bottom-right',
                autoClose: 3000
            });

            // Navigate to assignment list
            navigate(`/${companySlug}/assignment/list`);
        } catch (error) {
            console.error('Error deleting job:', error);
            Toast.error(error.message || 'Failed to delete job', {
                title: 'Error',
                position: 'bottom-right',
                autoClose: 5000
            });
            setIsDeletingJob(false);
        }
    };

    const openAssignDesignerModal = async (quoteType, childQuoteId = null, childQuoteName = null) => {
        setSelectedQuoteType(quoteType);
        setSelectedChildQuoteId(childQuoteId);
        setSelectedChildQuoteName(childQuoteName);
        try {
            const designers = await getAvailableDesigners(company.id);
            setAvailableDesigners(designers);
            setShowDesignerModal(true);
        } catch (error) {
            console.error("Error fetching designers:", error);
            alert("Error loading designers");
        }
    };

    const openAssignEstimatorModal = async () => {
        try {
            const estimators = await getAvailableEstimators(company.id);
            setAvailableEstimators(estimators);
            setShowEstimatorModal(true);
        } catch (error) {
            console.error("Error fetching estimators:", error);
            alert("Error loading estimators");
        }
    };

    const openAssignDriverModal = async (quoteType, deliveryCount = 0, childQuoteId = null, childQuoteName = null) => {
        setSelectedQuoteTypeForDriver(quoteType);
        setSelectedDeliveryCount(deliveryCount);
        setSelectedChildQuoteIdForDriver(childQuoteId);
        setSelectedChildQuoteNameForDriver(childQuoteName);
        try {
            const drivers = await getAvailableDrivers(company.id);
            setAvailableDrivers(drivers);
            setShowDriverModal(true);
        } catch (error) {
            console.error("Error fetching drivers:", error);
            alert("Error loading drivers");
        }
    };

    const handleAssignDriver = async () => {
        if (!selectedDriverId || !selectedQuoteTypeForDriver) return;

        try {
            // Parse the selected driver ID (format: "id_name")
            const [driverId, driverName] = selectedDriverId.split('_');

            await assignDriver(
                assignment.id,
                selectedQuoteTypeForDriver,
                driverId,
                driverName,
                selectedDeliveryCount,
                department?.id,
                department?.child_unique_id
            );

            await sendDriverSMS(
                assignment,
                company,
                driverId,
                selectedQuoteTypeForDriver
            );

            const label = selectedChildQuoteNameForDriver || assignment.unique_id;
            const notificationData = {
                user_id: driverId,
                company_id: company.id,
                type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
                title: "New Delivery Assignment",
                message: `Assignment ${label} (${selectedQuoteTypeForDriver}) assigned to you by ${userProfile.full_name || userProfile.username}`,
                priority: "normal",
                related_type: "assignment",
                related_id: assignment.id,
                action_url: `${companySlug}/assignment/${assignment.id}`
            };

            await createNotification(notificationData);

            setShowDriverModal(false);
            setSelectedDriverId('');
            setSelectedQuoteTypeForDriver(null);
            setSelectedChildQuoteIdForDriver(null);
            setSelectedChildQuoteNameForDriver(null);
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning driver:", error);
            alert(error.message || "Error assigning driver");
        }
    };

    // childQuoteId is the job_quotes UUID — null for old single-quote jobs
    const handleRemoveDriver = async (quoteType, deliveryCount = 0) => {
        if (!window.confirm("Are you sure you want to remove this driver?")) return;

        try {
            await removeDriver(assignment.id, quoteType, deliveryCount, department?.id);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing driver:", error);
            alert(error.message || "Error removing driver");
        }
    };

    const handleConfirmPackage = async (quoteType, deliveryCount = 0) => {
        try {
            await confirmPackageReceipt(assignment.id, quoteType, deliveryCount);
            // Get the expected delivery date for this specific quote
            const quote = assignment?.job_quotes?.find(q => q.quote_type === quoteType);
            const deliveryDate = quote?.expected_delivery_date || null;
            await sendInTransitNotificationSMS(assignment?.customer_number, assignment?.customer_name, assignment?.short_id, assignment?.id, deliveryDate, company, assignment?.unique_id) // phoneNumber, customerName, shortId, jobId, deliveryDate, company, jobName
            fetchAssignment();
        } catch (error) {
            console.error("Error confirming package:", error);
            alert(error.message || "Error confirming package receipt");
        }
    };

    const handleEndDelivery = async (quoteType, deliveryCount = 0) => {
        if (!window.confirm("Are you sure you want to mark this delivery as completed?")) return;

        try {
            const deliveryKey = `${quoteType}-${deliveryCount}`;
            await completeDelivery(
                assignment.id,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                deliveryNotes[deliveryKey] || '',
                deliveryCount
            );

            const quote = assignment?.job_quotes?.find(q => q.quote_type === quoteType);
            const deliveryDate = quote?.expected_delivery_date || null;

            await sendDeliveredNotificationSMS(assignment?.customer_number, assignment?.customer_name, assignment?.short_id, assignment?.id, deliveryDate, company, assignment?.unique_id) // phoneNumber, customerName, shortId, jobId, deliveryDate, company, jobName
            fetchAssignment();
        } catch (error) {
            console.error("Error completing delivery:", error);
            alert(error.message || "Error completing delivery");
        }
    };

    const handleUploadDeliveryPhotos = async (quoteType, deliveryCount) => {
        const deliveryKey = `${quoteType}-${deliveryCount}`;
        if (!uploadedDeliveryPhotos[deliveryKey] || uploadedDeliveryPhotos[deliveryKey].length === 0) {
            alert("Please select at least one photo to upload");
            return;
        }

        setIsSubmittingDelivery(true);

        try {
            const photoFiles = uploadedDeliveryPhotos[deliveryKey];
            const uploadedFileRecords = [];

            // Upload each photo
            for (const file of photoFiles) {
                const fileRecord = await uploadDeliveryPhoto(
                    assignment.id,
                    file,
                    quoteType,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    userProfile.role,
                    companySlug,
                    deliveryCount
                );
                uploadedFileRecords.push(fileRecord);
            }

            // Update delivery notes if provided
            if (deliveryNotes[deliveryKey]?.trim()) {
                let query = supabase
                    .from('job_deliveries')
                    .update({
                        delivery_notes: deliveryNotes[deliveryKey].trim(),
                        note_updated_at: new Date().toISOString(),
                        note_updated_by_user_id: userProfile.id,
                        note_updated_by_name: userProfile.username || userProfile.full_name
                    })
                    .eq('job_id', assignment.id)
                    .eq('quote_type', quoteType);

                // Handle both null and 0 for delivery_count (backwards compatibility)
                if (deliveryCount === 0) {
                    query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
                } else {
                    query = query.eq('delivery_count', deliveryCount);
                }

                const { error: notesError } = await query;
                if (notesError) throw notesError;
            }

            // Clear the upload state
            setUploadedDeliveryPhotos(prev => ({
                ...prev,
                [deliveryKey]: []
            }));

            setClearDeliveryPhotos(prev => ({
                ...prev,
                [deliveryKey]: true
            }));

            setTimeout(() => {
                setClearDeliveryPhotos(prev => ({
                    ...prev,
                    [deliveryKey]: false
                }));
            }, 100);

            alert(`Successfully uploaded ${uploadedFileRecords.length} photo(s)`);
            fetchAssignment();
        } catch (error) {
            console.error("Error uploading delivery photos:", error);
            alert(error.message || "Error uploading delivery photos");
        } finally {
            setIsSubmittingDelivery(false);
        }
    };

    const handleDeliveryPhotosChange = (quoteType, deliveryCount) => (files) => {
        const deliveryKey = `${quoteType}-${deliveryCount}`;
        setUploadedDeliveryPhotos(prev => ({
            ...prev,
            [deliveryKey]: files
        }));
    };

    const handleAssignDesigner = async () => {
        if (!selectedDesignerId || !selectedQuoteType) return;

        try {
            const designer = availableDesigners.find(d => d.id === selectedDesignerId);
            await assignDesigner(
                assignment.id,
                selectedQuoteType,
                selectedDesignerId,
                designer.username || designer.full_name,
                selectedChildQuoteId || null,
                selectedChildQuoteName || null
            );

            let url = `${companySlug}/assignment/${assignment.id}`;
            if (selectedChildQuoteId) url += `/${selectedChildQuoteId}`;

            const notificationData = {
                user_id: selectedDesignerId,
                company_id: company.id,
                type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
                title: "New Assignment",
                message: `Assignment ${selectedChildQuoteName || assignment.unique_id} (${selectedQuoteType}) assigned to you by ${userProfile.full_name || userProfile.username}`,
                priority: "normal",
                related_type: "assignment",
                related_id: assignment.id,
                action_url: url
            }

            await createNotification(notificationData)

            setShowDesignerModal(false);
            setSelectedDesignerId('');
            setSelectedQuoteType(null);
            setSelectedChildQuoteId(null);
            setSelectedChildQuoteName(null);
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning designer:", error);
            alert(error.message || "Error assigning designer");
        }
    };

    const handleSelfAssignDesigner = async (quoteType, childId, childName) => {
        try {
            await assignDesigner(
                assignment.id,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                childId || null,
                childName || null
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error self-assigning:", error);
            alert(error.message || "Error assigning yourself");
        }
    };

    // childQuoteId is the job_quotes UUID — null for old single-quote jobs,
    // set for new multi-quote jobs so the backend can target the exact row.
    const handleRemoveDesigner = async (quoteType, designerId, childQuoteId = null) => {
        if (!window.confirm("Are you sure you want to remove this designer?")) return;

        try {
            await removeDesigner(assignment.id, quoteType, designerId, childQuoteId);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing designer:", error);
            alert(error.message || "Error removing designer");
        }
    };

    const handleAssignEstimator = async () => {
        if (!selectedEstimatorId) return;

        try {
            const estimator = availableEstimators.find(e => e.id === selectedEstimatorId);
            await assignEstimator(
                assignment.id,
                selectedEstimatorId,
                estimator.username || estimator.full_name,
            );

            const notificationData = {
                user_id: selectedEstimatorId,
                company_id: company.id,
                type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
                title: "New Assignment",
                message: `Assignment ${assignment.unique_id} assigned to you by ${userProfile.full_name || userProfile.username}`,
                priority: "normal",
                related_type: "assignment",
                related_id: assignment.id,
                action_url: `${companySlug}/assignment/${assignment.id}`
            }

            await createNotification(notificationData)

            setShowEstimatorModal(false);
            setSelectedEstimatorId('');
            fetchAssignment();
        } catch (error) {
            console.error("Error assigning estimator:", error);
            alert(error.message || "Error assigning estimator");
        }
    };

    const handleSelfAssignEstimator = async () => {
        try {
            await assignEstimator(
                assignment.id,
                userProfile.id,
                userProfile.username || userProfile.full_name
            );
            fetchAssignment();
        } catch (error) {
            console.error("Error self-assigning estimator:", error);
            alert(error.message || "Error assigning yourself as estimator");
        }
    };

    const handleRemoveEstimator = async () => {
        if (!window.confirm("Are you sure you want to remove the estimator?")) return;

        try {
            await removeEstimator(assignment.id);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing estimator:", error);
            alert(error.message || "Error removing estimator");
        }
    };

    const handleCompleteDesign = async (quoteType, assignedDesigners) => {

        let assignedDesigner = assignedDesigners.find(dUser => dUser?.quote_type === quoteType)

        if (!quoteType || !uploadedFiles?.pdfFiles || uploadedFiles.pdfFiles.length === 0) {
            alert("Please upload at least one file before completing the design");
            return;
        }

        if (!window.confirm(`Are you sure you want to complete the ${quoteType} design? This action cannot be undone.`)) {
            return;
        }

        setIsSubmittingCompletion(true);

        try {
            const result = await completeDesign(
                assignment.id,
                quoteType,
                assignedDesigner?.id || null,
                assignedDesigner?.name || null,
                completionNotes[quoteType] || '',
                uploadedFiles.pdfFiles,
                companySlug || company?.slug,
                userProfile,
                department
            );

            // Save delivery note to job_quotes if provided
            if (quoteDeliveryNotes[quoteType]?.trim()) {
                const { error: noteError } = await supabase
                    .from('job_quotes')
                    .update({ delivery_note: quoteDeliveryNotes[quoteType].trim() })
                    .eq('job_id', assignment.id)
                    .eq('quote_type', quoteType)
                    .or(`delivery_count.is.null,delivery_count.eq.0`);

                if (noteError) {
                    console.error('Error saving delivery note:', noteError);
                    // Don't throw - design is already completed
                }
            }

            // Success feedback
            const uploadCount = result.uploaded_files?.length || 0;
            alert(`Design completed successfully! ${uploadCount} file(s) uploaded.`);

            // Reset form
            setUploadedFiles({ pdfFiles: [] });
            setCompletionNotes(prev => ({
                ...prev,
                [quoteType]: ''
            }));
            setQuoteDeliveryNotes(prev => ({
                ...prev,
                [quoteType]: ''
            }));
            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 100);

            // Refresh assignment data
            fetchAssignment();
        } catch (error) {
            console.error("Error completing design:", error);
            alert(error.message || "Error completing design. Please try again.");
        } finally {
            setIsSubmittingCompletion(false);
        }
    };

    const handleRemoveSelfAssignment = async (type, quoteType) => {
        if (!window.confirm("Are you sure you want to remove this assignment from yourself?")) return;

        try {
            await removeSelfAssignment(assignment.id, assignment.unique_id, userProfile, type, quoteType, department);
            fetchAssignment();
        } catch (error) {
            console.error("Error removing designer:", error);
            alert(error.message || "Error removing designer");
        }
    }

    const handleCompleteEstimation = async (estimator) => {


        if (!uploadedFiles?.pdfFiles || uploadedFiles.pdfFiles.length === 0) {
            alert("Please upload at least one file before completing the estimation");
            return;
        }

        if (!window.confirm("Are you sure you want to complete the estimation? This action cannot be undone.")) {
            return;
        }

        setIsSubmittingCompletion(true);

        try {
            const result = await completeEstimation(
                assignment.id,
                estimator?.user_id || null,
                estimator?.name || null,
                completionNotes.estimation || '',
                uploadedFiles.pdfFiles,
                companySlug || company?.slug,
                userProfile
            );

            // Success feedback
            const uploadCount = result.uploaded_files?.length || 0;
            alert(`Estimation completed successfully! ${uploadCount} file(s) uploaded.`);

            // Reset form
            setUploadedFiles({ pdfFiles: [] });
            setCompletionNotes(prev => ({
                ...prev,
                estimation: ''
            }));
            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 100);

            // Refresh assignment data
            fetchAssignment();
        } catch (error) {
            console.error("Error completing estimation:", error);
            alert(error.message || "Error completing estimation. Please try again.");
        } finally {
            setIsSubmittingCompletion(false);
        }
    };

    const handlePdfsChange = useCallback((pdfFiles) => {
        setUploadedFiles(prevState => ({ ...prevState, pdfFiles }));
    }, []);


    const handleCloseNewDeliveryModal = () => {
        setShowNewDeliveryModal(false);
        setNewDeliveryQuoteType(null);
        setNewDeliveryNote('');
        setNewDeliveryTimeSlot(null);
        setNewDeliveryScheduledDate('');
        setNewDeliverySelectedUnits([]);
    };

    const handleToggleUnit = (unitNumber) => {
        setNewDeliverySelectedUnits(prev => {
            if (prev.includes(unitNumber)) {
                return prev.filter(u => u !== unitNumber);
            } else {
                return [...prev, unitNumber];
            }
        });
    };

    const handleSubmitNewDelivery = async () => {
        try {
            if (!newDeliveryQuoteType) {
                alert('Quote type is required');
                return;
            }

            // Create the new delivery record
            await createNewDelivery(
                assignment.id,
                newDeliveryQuoteType,
                newDeliveryNote,
                newDeliveryTimeSlot,
                newDeliveryScheduledDate,
                newDeliverySelectedUnits
            );

            // Close modal and refresh assignment data
            handleCloseNewDeliveryModal();
            await fetchAssignment();

            alert('New delivery created successfully!');
        } catch (error) {
            console.error('Error creating new delivery:', error);
            alert('Failed to create new delivery. Please try again.');
        }
    };

    const getStatusColor = (status) => {
        const statusColors = {
            pending_design: "bg-yellow-700 text-white border-yellow-700",
            waiting_assignment: "bg-yellow-700 text-white border-yellow-700",
            in_design: "bg-yellow-700 text-white border-yellow-700",
            in_progress: "bg-yellow-700 text-white border-yellow-700",
            design_completed: "bg-green-600 text-white border-green-700",
            pending_estimation: "bg-orange-100 text-orange-800 border-orange-200",
            in_estimation: "bg-yellow-700 text-white border-yellow-700",
            estimation_completed: "bg-green-600 text-white border-green-700",
            ready_for_delivery: "bg-emerald-100 text-emerald-800 border-emerald-200",
            completed: "bg-green-600 text-white border-green-700"
        };
        return statusColors[status] || "bg-gray-100 text-gray-800 border-gray-200";
    };

    const getStatusTextColor = (status) => {
        const statusColors = {
            pending_design: "text-yellow-700",
            waiting_assignment: "text-yellow-700",
            in_design: "text-yellow-700",
            in_progress: "text-yellow-700",
            design_completed: "text-green-600",
            pending_estimation: "text-orange-100",
            in_estimation: "text-yellow-700",
            estimation_completed: "text-green-600",
            ready_for_delivery: "text-emerald-100",
            completed: "text-green-600"
        };
        return statusColors[status] || "text-gray-500";
    };

    const getDepartmentBoxColor = (status) => {
        const statusColors = {
            roof: "bg-blue-50",
            wall: "bg-red-50",
            floor: "bg-green-50",
        };
        return statusColors[status] || "bg-gray-200";
    };

    const getDepartmentTextColor = (status) => {
        const statusColors = {
            roof: "text-blue-900",
            wall: "text-red-900",
            floor: "text-green-900",
        };
        return statusColors[status] || "text-gray-900";
    };

    const getRequiredSections = (job) => {
        const sections = [];
        job.job_quotes?.forEach(quote => {
            if (quote.is_required) {
                if (quote?.child_unique_id) {
                    sections.push(quote?.child_unique_id);
                } else {
                    sections.push(quote.quote_type.charAt(0).toUpperCase() + quote.quote_type.slice(1));
                }

            }
        });
        return sections;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDateDriver = (dateString) => {
        if (!dateString) return "Not set";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const downloadFile = async (file) => {
        try {
            // The file.filename already contains the full path: companySlug/jobId/type-timestamp-filename
            let filePath;

            // Handle different filename formats from database
            if (file?.filename.startsWith('http://') || file?.filename.startsWith('https://')) {
                // If it's a full URL, extract the path after the bucket name
                const urlParts = file?.filename.split('/storage/v1/object/public/job-files/');
                if (urlParts.length > 1) {
                    filePath = decodeURIComponent(urlParts[1]);
                } else {
                    throw new Error('Invalid file URL format');
                }
            } else if (file?.filename.includes(`${companySlug || company?.slug}/${jobId}`)) {
                // Already has the full path structure
                filePath = file?.filename;
            } else if (file?.filename.startsWith(`${companySlug || company?.slug}/`)) {
                // Has company but might be missing jobId
                filePath = file?.filename;
            } else {
                // Just a filename or timestamp-based name, construct full path
                filePath = `${companySlug || company?.slug}/${jobId}/${file?.filename}`;
            }

            const { data, error } = await supabase.storage
                .from('job-files')
                .download(filePath);

            if (error) {
                console.error('Download error:', error);
                throw error;
            }

            // Create a download link
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.original_name || filePath.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
            alert('Failed to download file: ' + (err.message || 'Unknown error'));
        }
    };

    const downloadAllFilesWithFolders = async (files, zipName = 'files.zip') => {
        if (isDownloading) return;
        setIsDownloading(true);

        try {
            const zip = new JSZip();
            let successCount = 0;
            let failCount = 0;

            // Download and organize files
            for (const file of files) {
                try {
                    let filePath;

                    // Handle different filename formats
                    if (file?.filename.startsWith('http://') || file?.filename.startsWith('https://')) {
                        const urlParts = file?.filename.split('/storage/v1/object/public/job-files/');
                        if (urlParts.length > 1) {
                            filePath = decodeURIComponent(urlParts[1]);
                        } else {
                            throw new Error('Invalid file URL format');
                        }
                    } else if (file?.filename.includes(`${companySlug || company?.slug}/${jobId}`)) {
                        filePath = file?.filename;
                    } else if (file?.filename.startsWith(`${companySlug || company?.slug}/`)) {
                        filePath = file?.filename;
                    } else {
                        filePath = `${companySlug || company?.slug}/${jobId}/${file?.filename}`;
                    }

                    const { data, error } = await supabase.storage
                        .from('job-files')
                        .download(filePath);

                    if (error) throw error;

                    const fileName = file.original_name || filePath.split('/').pop();
                    const fileType = file.file_type || 'other';

                    // Create folder path based on file type
                    let folderPath;

                    if (fileType === 'design') {
                        // Design files: organize by quote type
                        const quoteType = file.quote_type || 'general';
                        const quoteTypeName = quoteType === 'general' ? 'General' :
                            quoteType.charAt(0).toUpperCase() + quoteType.slice(1);
                        folderPath = `Design/${quoteTypeName}/${fileName}`;
                    } else {
                        // Non-design files: only file type folder
                        const fileTypeName = fileType === 'initial' ? 'Job Files' :
                            fileType === 'estimation' ? 'Estimation' :
                                fileType === 'timber' ? 'Timber' :
                                    fileType === 'delivery' ? 'Delivery' :
                                        fileType.charAt(0).toUpperCase() + fileType.slice(1);
                        folderPath = `${fileTypeName}/${fileName}`;
                    }

                    zip.file(folderPath, data);
                    successCount++;

                } catch (err) {
                    console.error(`Error downloading file ${file.original_name}:`, err);
                    failCount++;
                } finally {
                    setIsDownloading(false);
                }
            }

            if (successCount === 0) {
                alert('Failed to download any files');
                return;
            }

            const zipBlob = await zip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
                compressionOptions: {
                    level: 6
                }
            });

            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = zipName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            if (failCount > 0) {
                alert(`Downloaded ${successCount} files. ${failCount} files failed.`);
            }

        } catch (err) {
            console.error('Error creating zip file:', err);
            alert('Failed to create zip file: ' + (err.message || 'Unknown error'));
        }
    };

    const seeFile = (file, allFiles = []) => {
        setSelectedFile(file);
        setViewerFiles(allFiles.length > 0 ? allFiles : [file]);
        setSelectedFileIndex(allFiles.findIndex(f => f.filename === file.filename));
        setFileViewerOpen(true);
    };

    // Create a custom-named folder (persisted via a placeholder file record)
    const handleCreateCustomFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return;
        setIsCreatingFolder(true);
        try {
            const { error } = await supabase
                .from('job_files')
                .insert({
                    job_id: assignment.id,
                    filename: '__folder_placeholder__',
                    original_name: name,
                    url: '__folder_placeholder__',
                    uploaded_by_user_id: userProfile.id,
                    uploaded_by_name: userProfile.username || userProfile.full_name,
                    uploaded_by_role: userProfile.role,
                    file_type: 'custom',
                    description: name,
                });
            if (error) throw error;
            setNewFolderName('');
            setShowFolderInput(false);
            fetchAssignment();
            Toast.success(`Folder "${name}" created`, { position: 'bottom-right', autoClose: 2000 });
        } catch (err) {
            console.error('Error creating folder:', err);
            Toast.error(err.message || 'Failed to create folder', { position: 'bottom-right', autoClose: 3000 });
        } finally {
            setIsCreatingFolder(false);
        }
    };

    // Trigger the hidden file input for a specific folder context
    const triggerFileUpload = (fileType, quoteType = null, folderName = null, isCustom = false) => {
        uploadContext.current = { fileType, quoteType, folderName, isCustom };
        if (fileUploadInputRef.current) {
            fileUploadInputRef.current.value = '';
            fileUploadInputRef.current.click();
        }
    };

    const handleHiddenFileInputChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const { fileType, quoteType, folderName, isCustom } = uploadContext.current;

        for (const file of files) {
            await uploadSingleFile(file, fileType, quoteType, folderName, isCustom);
        }

        e.target.value = '';
    };

    // Core upload logic — used by both file input and drag-drop
    const uploadSingleFile = async (file, fileType, quoteType, folderName, isCustom) => {
        const uploadKey = `${fileType}-${quoteType || 'general'}-${folderName || ''}`;
        setFolderUploading(uploadKey);
        try {
            if (isCustom && folderName) {
                const timestamp = Date.now();
                const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                const filename = `${companySlug || company?.slug}/${assignment.id}/custom-${timestamp}-${sanitizedFileName}`;
                const { error: storageError } = await supabase.storage
                    .from('job-files')
                    .upload(filename, file, { cacheControl: '3600', upsert: false });
                if (storageError) throw storageError;
                const { error: dbError } = await supabase
                    .from('job_files')
                    .insert({
                        job_id: assignment.id,
                        filename,
                        original_name: file.name,
                        url: filename,
                        uploaded_by_user_id: userProfile.id,
                        uploaded_by_name: userProfile.username || userProfile.full_name,
                        uploaded_by_role: userProfile.role,
                        file_type: 'custom',
                        description: folderName,
                    });
                if (dbError) throw dbError;
            } else {
                await uploadJobFile(
                    assignment.id,
                    file,
                    fileType,
                    quoteType,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    userProfile.role,
                    companySlug || company?.slug,
                    null
                );
            }
            fetchAssignment();
            Toast.success('File uploaded successfully', { position: 'bottom-right', autoClose: 2000 });
        } catch (err) {
            console.error('Error uploading file:', err);
            Toast.error(err.message || 'Failed to upload file', { position: 'bottom-right', autoClose: 3000 });
        } finally {
            setFolderUploading(null);
        }
    };

    const getDriverAssignedDeliveries = () => {
        if (userProfile?.role !== 'driver') return [];

        // Return all deliveries assigned to this driver (including multiple deliveries of same quote type)
        return assignment.job_deliveries?.filter(delivery =>
            delivery.assigned_driver_id === userProfile.id
        ).sort((a, b) => {
            // Sort by quote type first, then by delivery_count
            if (a.quote_type !== b.quote_type) {
                return a.quote_type.localeCompare(b.quote_type);
            }
            return (a.delivery_count ?? 0) - (b.delivery_count ?? 0);
        }) || [];
    };

    const canDesignerCompleteQuote = (quoteType) => {
        if (userProfile?.role !== 'designer') return false;

        const userDesigner = assignment.job_designers?.find(
            d => d.quote_type === quoteType && d.user_id === userProfile.id
        );
        return userDesigner && !userDesigner.is_completed;
    };

    // Status Badge Component
    const StatusBadge = ({ status, large = false }) => {
        const isTimber = assignment?.job_type === "Timber";
        const displayStatus = isTimber
            ? (status === 'ready_for_delivery' ? 'Ready for Delivery' :
                status === 'completed' ? 'Completed' : status.replace('_', ' '))
            : status.replace('_', ' ');

        return (
            <span className={`px-3 py-1 rounded-full font-medium border ${large ? 'text-sm' : 'text-xs'} ${getStatusColor(status)}`}>
                {displayStatus.toUpperCase()}
            </span>
        );
    };

    const INVOICE_META = {
        not_invoiced: {
            label: "Not Invoiced",
            short: "Not Inv",
            card: "bg-red-50 border-red-200",
            iconWrap: "bg-red-100 text-red-700",
            badge: "bg-red-100 text-red-700 border-red-200",
            accent: "bg-red-500",
            wrap: "bg-red-50 border-red-200",
        },
        null: {
            label: "Not Invoiced",
            short: "Not Inv",
            card: "bg-red-50 border-red-200",
            iconWrap: "bg-red-100 text-red-700",
            badge: "bg-red-100 text-red-700 border-red-200",
            accent: "bg-red-500",
            wrap: "bg-red-50 border-red-200",
        },
        undefined: {
            label: "Not Invoiced",
            short: "Not Inv",
            card: "bg-red-50 border-red-200",
            iconWrap: "bg-red-100 text-red-700",
            badge: "bg-red-100 text-red-700 border-red-200",
            accent: "bg-red-500",
            wrap: "bg-red-50 border-red-200",
        },
        deleted: {
            label: "Not Invoiced",
            short: "Not Inv",
            card: "bg-red-50 border-red-200",
            iconWrap: "bg-red-100 text-red-700",
            badge: "bg-red-100 text-red-700 border-red-200",
            accent: "bg-red-500",
            wrap: "bg-red-50 border-red-200",
        },

        draft: {
            label: "DRAFT",
            short: "DRAFT",
            card: "bg-sky-50 border-sky-200",
            iconWrap: "bg-sky-100 text-sky-700",
            badge: "bg-sky-100 text-sky-700 border-sky-200",
            accent: "bg-sky-500",
            wrap: "bg-sky-50 border-sky-200",
        },
        submitted: {
            label: "Submitted",
            short: "Submitted",
            card: "bg-sky-50 border-sky-200",
            iconWrap: "bg-sky-100 text-sky-700",
            badge: "bg-sky-100 text-sky-700 border-sky-200",
            accent: "bg-sky-500",
            wrap: "bg-sky-50 border-sky-200",
        },

        invoiced: {
            label: "Invoiced",
            short: "Inv",
            card: "bg-blue-50 border-blue-200",
            iconWrap: "bg-blue-100 text-blue-700",
            badge: "bg-blue-100 text-blue-700 border-blue-200",
            accent: "bg-blue-500",
            wrap: "bg-blue-50 border-blue-200",
        },
        authorised: {
            label: "Authorised",
            short: "Authorised",
            card: "bg-blue-50 border-blue-200",
            iconWrap: "bg-blue-100 text-blue-700",
            badge: "bg-blue-100 text-blue-700 border-blue-200",
            accent: "bg-blue-500",
            wrap: "bg-blue-50 border-blue-200",
        },
        cod: {
            label: "COD",
            short: "COD",
            card: "bg-sky-50 border-sky-200",
            iconWrap: "bg-sky-100 text-sky-700",
            badge: "bg-sky-100 text-sky-700 border-sky-200",
            accent: "bg-sky-500",
            wrap: "bg-sky-50 border-sky-200",
        },
        paid: {
            label: "Paid",
            short: "Paid",
            card: "bg-green-50 border-green-200",
            iconWrap: "bg-green-100 text-green-700",
            badge: "bg-green-100 text-green-700 border-green-200",
            accent: "bg-green-500",
            wrap: "bg-green-50 border-green-200",
        },
        deposit: {
            label: "Deposit",
            short: "Deposit",
            card: "bg-amber-50 border-amber-200",
            iconWrap: "bg-amber-100 text-amber-700",
            badge: "bg-amber-100 text-amber-700 border-amber-200",
            accent: "bg-amber-500",
            wrap: "bg-amber-50 border-amber-200",
        },
        voided: {
            label: "Voided",
            short: "Voided",
            card: "bg-zinc-50 border-zinc-200",
            iconWrap: "bg-zinc-100 text-zinc-700",
            badge: "bg-zinc-100 text-zinc-700 border-zinc-200",
            accent: "bg-zinc-500",
            wrap: "bg-zinc-50 border-zinc-200",
        },
        cancelled: {
            label: "Cancelled",
            short: "Cancelled",
            card: "bg-zinc-50 border-zinc-200",
            iconWrap: "bg-zinc-100 text-zinc-700",
            badge: "bg-zinc-100 text-zinc-700 border-zinc-200",
            accent: "bg-zinc-500",
            wrap: "bg-zinc-50 border-zinc-200",

        },
    };

    const getSortedQuoteTypeEntries = (rows = []) => {
        const grouped = rows.reduce((acc, row) => {
            const type = row?.quote_type || "other";
            if (!acc[type]) acc[type] = [];
            acc[type].push(row);
            return acc;
        }, {});

        return Object.entries(grouped)
            .sort(([a], [b]) => (QUOTE_ORDER[a] ?? 99) - (QUOTE_ORDER[b] ?? 99))
            .map(([quoteType, items]) => ({
                quoteType,
                label: getQuoteLabel(quoteType),
                items: items.sort((a, b) => {
                    const childCompare = (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                    if (childCompare !== 0) return childCompare;
                    return (a.delivery_count || 0) - (b.delivery_count || 0);
                }),
            }));
    };
    const openXeroInvoicePdf = async (quote) => {
        if (!quote?.xero_invoice_id) return;

        try {
            setXeroPdfModal({
                open: true,
                loading: true,
                error: null,
                pdfUrl: null,
                title: quote?.xero_invoice_number || quote?.child_unique_id || 'Xero Invoice',
            });

            const res = await fetch(
                `https://xgezccwtibbfoplffzcr.supabase.co/functions/v1/xero-invoice-pdf?invoice_id=${quote.xero_invoice_id}&company_id=${company.id}`
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to load Xero invoice PDF');
            }

            const blob = await res.blob();
            const pdfUrl = URL.createObjectURL(blob);

            setXeroPdfModal(prev => ({
                ...prev,
                loading: false,
                pdfUrl,
            }));
        } catch (err) {
            setXeroPdfModal(prev => ({
                ...prev,
                loading: false,
                error: err.message || 'Failed to load invoice PDF',
            }));
        }
    };

    const closeXeroPdfModal = () => {
        if (xeroPdfModal.pdfUrl) {
            URL.revokeObjectURL(xeroPdfModal.pdfUrl);
        }

        setXeroPdfModal({
            open: false,
            loading: false,
            error: null,
            pdfUrl: null,
            title: '',
        });
    };


    const isSiteMeasureRequired = assignment?.site_measure_required && (
        (department?.quote_type === 'roof' && assignment.site_measure_truss) ||
        (department?.quote_type === 'wall' && assignment.site_measure_wall) ||
        (department?.quote_type === 'floor' && assignment.site_measure_floor)
    );

    // Info Card Component
    const InfoCard = ({ title, children, icon: Icon, color = "blue", customClass = "" }) => (
        <div className={`bg-white border border-gray-200 rounded-lg p-4 space-y-2 ${customClass}`}>
            <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                <Icon className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
            </div>
            <div className="space-y-2">
                {children}
            </div>
        </div>
    );

    // Detail Row Component
    const DetailRow = ({ label, value, icon: Icon, link, extraStyle = "", containerStyle = "" }) => (
        <div className={`flex items-start gap-2 ${containerStyle}`}>
            {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />}
            <div className="flex-1 min-w-0 mb-1">
                <div className="text-xs text-gray-500">{label}</div>
                {link ? (
                    <a href={link} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-gray-800 break-words hover:text-blue-600 flex">
                        {value || "Not specified"}
                    </a>
                ) : (
                    <div className={`text-sm font-medium text-gray-800 break-words ${extraStyle}`}>{value || "Not specified"}</div>
                )}
            </div>
        </div>
    );

    // Checkbox Display Component
    const CheckboxDisplay = ({ label, isChecked, keyText }) => (
        <div key={keyText || label} className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isChecked ? 'bg-green-500 border-green-500' : 'border-gray-300'
                }`}>
                {isChecked && <FiCheck className="w-3 h-3 text-white" />}
            </div>
            <span className={`text-sm ${isChecked ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                {label}
            </span>
        </div>
    );

    const getQuoteIcon = (quoteType) => {

        let icon = <FiLayers className="w-4 h-4" />
        if (quoteType === "roof") icon = <FiHome className="w-4 h-4" />
        if (quoteType === "wall") icon = <BrickWall className="w-4 h-4" />
        if (quoteType === "floor") icon = <Grid2X2 className="w-4 h-4" />

        return icon;
    }

    const StatusPill = ({ label, color = "neutral" }) => {

        const base =
            "px-3 py-1 rounded-full text-xs font-semibold border transition-all";

        const variants = {
            neutral: "bg-gray-800 text-white border-gray-800",
            success: "bg-green-600 text-white border-green-600",
            warning: "bg-amber-500 text-white border-amber-500",
            danger: "bg-red-600 text-white border-red-600",
            info: "bg-blue-600 text-white border-blue-600"
        };

        return (
            <span className={`${base} ${variants[color] || ""}`}>
                {label}
            </span>
        );
    };


    const DisplayStatusCards = ({ assignment }) => {
        const QUOTE_ORDER = { roof: 0, wall: 1, floor: 2, timber: 3 };
        const DEPT_LIMIT = 10;

        const [showAllDepts, setShowAllDepts] = useState(false);

        const requiredQuotes = (assignment.job_quotes?.filter(q =>
            q.is_required && !q.delivery_count
        ) || []).sort((a, b) => {
            const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
            if (typeOrder !== 0) return typeOrder;
            return (a.child_unique_id || '').localeCompare(b.child_unique_id || '');
        });

        const visibleQuotes = showAllDepts ? requiredQuotes : requiredQuotes.slice(0, DEPT_LIMIT);
        const hasMore = requiredQuotes.length > DEPT_LIMIT;

        const estimationStatus = getJobStatusSummary(assignment, 'estimation');
        const designStatus = getJobStatusSummary(assignment, 'design');

        return (
            <div className="flex flex-col gap-3 w-full">

                {/* 🔹 Main Status Row */}
                <div className="flex flex-wrap items-center gap-2">

                    {assignment.job_type === "Estimation" && (
                        <StatusPill
                            label={assignment?.on_hold ? "Estimation On Hold" : estimationStatus}
                            color="neutral"
                        />
                    )}


                    {assignment.job_type === "Design" && (
                        <StatusPill
                            label={designStatus}
                            color="neutral"
                        />
                    )}

                    {assignment.job_type === "Design & Estimation" && (
                        <>
                            <StatusPill
                                label={assignment?.on_hold ? "Est. On Hold" : `Est. ${estimationStatus}`}
                                color={assignment?.on_hold ? "neutral" : getStatusColor(estimationStatus?.toLowerCase().replaceAll(" ", "_"))}
                            />
                            <StatusPill
                                label={`Des. ${designStatus}`}
                                color={getStatusColor(designStatus?.toLowerCase().replaceAll(" ", "_"))}
                            />
                        </>
                    )}
                </div>

                {/* 🔹 Required Quotes */}
                {(assignment.job_type !== "Estimation" && requiredQuotes.length > 0) && (
                    <div className="flex flex-wrap gap-2">
                        {visibleQuotes.map((quote) => {
                            if (quote.id === departmentId) return;
                            const isChild = !!quote?.child_unique_id;
                            const pillClass = `flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-gray-100 border border-gray-200 capitalize ${getStatusTextColor(quote?.design_status)} ${isChild ? 'cursor-pointer hover:bg-gray-200 hover:border-gray-300 transition-colors' : ''}`;
                            const content = (
                                <>
                                    <span className="text-sm">
                                        {getQuoteIcon(quote?.quote_type)}
                                    </span>
                                    <span>
                                        {quote?.child_unique_id || quote?.quote_type}
                                    </span>
                                </>
                            );
                            return isChild ? (
                                <button
                                    key={quote?.child_unique_id}
                                    onClick={() => navigate(`/${companySlug}/assignment/${jobId}/${quote.id}`, {
                                        state: { from: location.pathname }
                                    })}
                                    className={pillClass}
                                >
                                    {content}
                                </button>
                            ) : (
                                <div
                                    key={quote?.quote_type}
                                    className={pillClass}
                                >
                                    {content}
                                </div>
                            );
                        })}
                        {hasMore && (
                            <button
                                onClick={() => setShowAllDepts(prev => !prev)}
                                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-dashed border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                            >
                                {showAllDepts
                                    ? 'Show less'
                                    : `+${requiredQuotes.length - DEPT_LIMIT} more`}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };



    const FILE_ICON_MAP = {
        image: {
            extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'],
            icon: <FiImage size={20} color="GRAY" />
        },
        pdf: {
            extensions: ['pdf'],
            icon: <FaRegFilePdf size={20} color="GRAY" />
        },
        word: {
            extensions: ['doc', 'docx'],
            icon: <FaRegFileWord size={20} color="GRAY" />
        },
        excel: {
            extensions: ['xls', 'xlsx', 'csv'],
            icon: <FileSpreadsheet size={20} color="GRAY" />
        },
        audio: {
            extensions: ['mp3', 'wav', 'ogg', 'flac', 'aac'],
            icon: <FileAudio size={20} color="GRAY" />
        },
        video: {
            extensions: ['mp4', 'mov', 'avi', 'mkv', 'webm'],
            icon: <FileVideo size={20} color="GRAY" />
        },
        archive: {
            extensions: ['zip', 'rar', '7z', 'tar', 'gz'],
            icon: <FileArchive size={20} color="GRAY" />
        },
        code: {
            extensions: ['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'py', 'java', 'php'],
            icon: <Code size={20} color="GRAY" />
        },
        text: {
            extensions: ['txt', 'md', 'log'],
            icon: <FiFileText size={20} color="GRAY" />
        }
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();

        for (const type in FILE_ICON_MAP) {
            if (FILE_ICON_MAP[type].extensions.includes(extension)) {
                return FILE_ICON_MAP[type].icon;
            }
        }

        return <FiFile size={20} color="GRAY" />; // default icon
    };

    const toggleFolder = (fileType, e) => {
        if (e) e.preventDefault();
        setOpenFolders(prev => ({
            ...prev,
            [fileType]: !prev[fileType]
        }));
    };

    const toggleQuoteFolder = (fileType, quoteType, e) => {
        if (e) e.preventDefault();
        const key = `${fileType}-${quoteType}`;
        setOpenQuoteFolders(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };


    const parseLogDetails = (details) => {
        if (!details) return 'No details';

        // If it's already a string and not JSON, return it
        if (typeof details === 'string') {
            try {
                // Try to parse as JSON
                const parsed = JSON.parse(details);
                return parsed.message || parsed.details || details;
            } catch {
                // Not JSON, return as is
                return details;
            }
        }

        // If it's an object, extract message or details
        if (typeof details === 'object') {
            return details.message || details.details || JSON.stringify(details);
        }

        return String(details);
    };


    const QUOTE_STATUS_COLORS = {
        completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
        in_progress: "bg-amber-100 text-amber-700 border-amber-200",
        waiting_design: "bg-blue-100 text-blue-700 border-blue-200",
        waiting_assignment: "bg-gray-100 text-gray-500 border-gray-200",
        not_required: "bg-slate-50 text-slate-400 border-slate-200",
    };
    const getQuoteColor = (designStatus) =>
        QUOTE_STATUS_COLORS[designStatus] || QUOTE_STATUS_COLORS.waiting_assignment;

    const renderQuoteChips = (job) => {
        const required = job.job_quotes?.filter(q => q.is_required) || [];
        // group by quote_type
        const grouped = required.reduce((acc, q) => {
            if (!acc[q.quote_type]) acc[q.quote_type] = [];
            acc[q.quote_type].push(q);
            return acc;
        }, {});

        const QUOTE_ICONS_STACK = {
            roof: <Home size={14} />,
            wall: <PanelsTopLeft size={14} />,
            floor: <HiOutlineSquare3Stack3D size={14} />,
            timber: <GiWoodBeam size={14} />,
        };

        const QUOTE_ORDER = { roof: 0, wall: 1, floor: 2, timber: 3 };

        return Object.entries(grouped)
            .sort(([typeA, quotesA], [typeB, quotesB]) => {
                const typeOrder =
                    (QUOTE_ORDER[typeA] ?? 99) - (QUOTE_ORDER[typeB] ?? 99);

                if (typeOrder !== 0) return typeOrder;

                const aChild = quotesA[0]?.child_unique_id || '';
                const bChild = quotesB[0]?.child_unique_id || '';

                return aChild.localeCompare(bChild);
            }).map(([type, quotes]) => {
                const count = quotes.length;

                return (
                    <div key={type} className="flex items-center gap-1 mr-2 mt-1">
                        {/* stacked icons */}
                        <div className="flex -space-x-[10px] bg-transparent">
                            {quotes.map((q, i) => (
                                <span
                                    key={i}
                                    className={`p-1 rounded-full ${getQuoteColor(q.design_status)} bg-zinc-100 drop-shadow-sm border-2 border-white`} // ${getQuoteColor(q.design_status)}
                                    style={{
                                        left: i * 12,
                                        zIndex: count - i   // 👈 first icon gets highest z-index
                                    }}
                                >
                                    {QUOTE_ICONS_STACK[type]}
                                </span>
                            ))}
                        </div>

                        {/* count */}
                        <span className="text-xs text-gray-500">
                            ({count})
                        </span>
                    </div>
                );
            });
    };

    const SUB_STATUS_COLORS = {
        completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
        in_progress: "bg-amber-50   text-amber-700   border-amber-200",
        in_estimation: "bg-amber-50   text-amber-700   border-amber-200",
        waiting_design: "bg-blue-50    text-blue-700    border-blue-200",
        waiting_assignment: "bg-gray-100   text-gray-500    border-gray-200",
        not_required: "bg-slate-50   text-slate-400   border-slate-200",
        in_cutting: "bg-orange-50  text-orange-700  border-orange-200",
        cutting_ended: "bg-orange-50  text-orange-700  border-orange-200",
        in_production: "bg-violet-50  text-violet-700  border-violet-200",
        not_started: "bg-gray-100   text-gray-400    border-gray-200",
    };
    const getSubStatusColor = (status) =>
        SUB_STATUS_COLORS[status] || "bg-gray-50 text-gray-500 border-gray-200";

    const renderStatusFields = (assignment, quote) => {
        if (!quote) return null;

        const jobType = assignment.job_type || "";
        const hasEstimation = jobType.includes("Estimation");
        const hasDesign =
            (jobType.includes("Design") && !jobType.includes("Estimation")) ||
            jobType === "Design & Estimation";

        const fields = [];

        if (hasEstimation) {
            const est = quote.estimation_status;
            if (est && est !== "not_required") {
                fields.push({
                    label: "Est.",
                    status: est,
                    text: est.replace(/_/g, " ")
                });
            }
        }

        if (jobType.includes("Design") || jobType === "Timber") {
            const des = quote.design_status;
            if (des && des !== "not_required") {
                fields.push({
                    label: "Design",
                    status: des,
                    text: des.replace(/_/g, " ")
                });
            }

            const prod = quote.production_status;
            if (prod && prod !== "not_required" && prod !== "not_started") {
                fields.push({
                    label: "Prod.",
                    status: prod,
                    text: prod.replace(/_/g, " ")
                });
            }
        }

        if (!fields.length) return null;

        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {fields.map((f, i) => (
                    <span
                        key={i}
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none ${getSubStatusColor(f.status)}`}
                    >
                        <span className="text-[9px] font-bold opacity-60 uppercase">
                            {f.label}
                        </span>
                        {f.text.charAt(0).toUpperCase() + f.text.slice(1)}
                    </span>
                ))}
                <span
                    key={quote?.id + "-invoiced"}
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border leading-none ${quote?.invoiced ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
                >
                    <span className="text-[9px] font-bold opacity-60 uppercase">
                        {"INV"}
                    </span>
                    {quote?.invoiced ? "Invoiced" : "Not Invoiced"}
                </span>
            </div>
        );
    };

    const formatAustralianDisplayPhone = (phone) => {
        if (!phone) return '';

        let digits = String(phone).replace(/\D/g, '');

        // Remove country code
        if (digits.startsWith('61')) {
            digits = digits.slice(2);
        }

        // Add local leading zero
        if (!digits.startsWith('0')) {
            digits = `0${digits}`;
        }

        // Format 04XX XXX XXX
        if (digits.length <= 4) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;

        return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
    };


    if (!assignment) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-10 h-10 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading assignment details...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <input
                ref={fileUploadInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleHiddenFileInputChange}
            />
            <div className="mobile-detail-page department-detail-page px-4 md:px-0">
                {/* Header */}
                <div
                    className="sticky top-0 z-20 bg-white border-b border-gray-200 transition-all duration-200"
                    style={isScrolled ? { boxShadow: "0 6px 20px rgba(0,0,0,0.06)" } : {}}
                >
                    <div className="px-4 md:px-6 py-3 flex flex-col gap-3">

                        {/* 🔹 Top Row */}
                        <div className="flex items-start justify-between gap-4">

                            <div className="flex items-start gap-3 flex-1">

                                <BackButtonIconOnly className="bg-transparent !text-blue-600 border border-blue-500 hover:!text-white w-10 h-10" />

                                <div className="flex flex-col">

                                    {/* 🔹 CHILD ID (PRIMARY) */}
                                    <h1 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight">
                                        <span className="px-2 py-0.5 rounded bg-blue-600 text-white mr-2 uppercase text-base">child</span>{department?.child_unique_id || assignment?.unique_id}
                                    </h1>

                                    {/* 🔸 Parent Context */}
                                    <button
                                        className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mt-1"
                                        onClick={() => navigate(`/${companySlug}/assignment/${jobId}`, {
                                            state: { from: location.pathname }
                                        })}
                                    >

                                        <span className="px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-gray-700 font-medium">
                                            Parent: {assignment?.unique_id}
                                        </span>

                                        <span className="opacity-70">
                                            #{assignment?.short_id}
                                        </span>

                                    </button>
                                </div>
                            </div>

                            {/* 🔹 Right Actions */}
                            <div className="flex items-center gap-2">

                                {department?.layout_only && (
                                    <span className="px-3 py-1 bg-purple-100 text-purple-700 border border-purple-200 rounded-full text-xs font-semibold">
                                        Layout Only
                                    </span>
                                )}
                                {(assignment.urgent_quote || assignment.urgent_design) && (
                                    <span className="px-3 py-1 bg-red-50 text-red-600 border border-red-200 rounded-full text-xs font-semibold">
                                        URGENT
                                    </span>
                                )}

                                <button
                                    onClick={() => navigate(`/${companySlug}/assignment/${jobId}/edit`, {
                                        state: { from: location.pathname }
                                    })}
                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                    <FiEdit3 className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* 🔹 Status Row */}
                        <div className="flex flex-wrap items-center gap-3">
                            <DisplayStatusCards assignment={assignment} />
                        </div>

                    </div>
                </div>

                <div className="space-y-4 mt-4">
                    {/* Main Content - Customer, Supervisor, etc. */}
                    <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                        <div className="flex flex-col gap-2 !h-full">
                            {/* Customer Information */}
                            <InfoCard title="Customer Information" icon={FiUser} color="blue">
                                <DetailRow label="Customer Name" value={assignment.customer_name} icon={FiUser} />
                                <DetailRow
                                    label="Phone Number"
                                    value={formatAustralianDisplayPhone(assignment.customer_number)}
                                    icon={FiPhone}
                                    link={assignment.customer_number && `tel:${assignment.customer_number}`}
                                />
                                <DetailRow
                                    label="Email Address"
                                    value={assignment.customer_mail}
                                    icon={FiMail}
                                    link={assignment.customer_mail && `mailto:${assignment.customer_mail}`}
                                />
                                <DetailRow
                                    label="Job Address"
                                    value={assignment?.customer_address}
                                    icon={FiMap}
                                    link={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment?.customer_address)}`}
                                />

                                <DropdownSection
                                    title="Supervisor Information"
                                    titleStyle="text-zinc-700 text-xs"
                                    icon={FiUser}
                                    iconStyle="text-zinc-800 !w-4 !h-4"
                                    subtext={``}
                                    subtextStyle="text-xs "
                                    defaultOpen={false}
                                >
                                    <DetailRow label="Supervisor Name" value={assignment.carpenter_supervisor_name} icon={FiUser} />
                                    <DetailRow
                                        label="Phone Number"
                                        value={formatAustralianDisplayPhone(assignment.carpenter_supervisor_number)}
                                        icon={FiPhone}
                                        link={assignment.carpenter_supervisor_number && `tel:${assignment.carpenter_supervisor_number}`}
                                    />
                                </DropdownSection>
                            </InfoCard>

                            {/* Timeline */}

                            <InfoCard title="Timeline" icon={FiCalendar} color="orange" customClass="flex-1">

                                {assignment.job_type !== "Design" &&
                                    <div className="pt-2 mt-2">
                                        {(() => {

                                            let assignedEstimator = assignment?.job_estimators;
                                            let date = null;
                                            let text = "No estimator assigned"

                                            if (assignedEstimator && assignedEstimator?.completed_at === null) text = "Estimation in progress"
                                            if (assignedEstimator && assignedEstimator?.completed_at !== null) date = assignedEstimator?.completed_at;

                                            return (
                                                <DetailRow label="Estimation Completion Date" value={date !== null ? formatDate(date) : text} icon={FiFlag} />
                                            )
                                        })()}
                                    </div>
                                }


                                {(() => {
                                    const QUOTE_COLORS = {
                                        roof: { bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-blue-500', label: 'text-blue-700', icon: <Home className="w-3 h-3" /> },
                                        wall: { bg: 'bg-red-50', border: 'border-red-200', iconBg: 'bg-red-500', label: 'text-red-700', icon: <PanelsTopLeft className="w-3 h-3" /> },
                                        floor: { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-500', label: 'text-green-700', icon: <HiOutlineSquare3Stack3D className="w-3 h-3" /> },
                                        timber: { bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-500', label: 'text-amber-700', icon: <GiWoodBeam className="w-3 h-3" /> },
                                    };
                                    const QUOTE_ORDER = { roof: 0, wall: 1, floor: 2, timber: 3 };

                                    const bookedQuotes = (assignment.job_quotes ?? []).filter(q => q.id === departmentId)
                                        .filter(q => q.is_required && (q.delivery_count === null || (q.delivery_count ?? 0) === 0))
                                        .filter(q => q.expected_finish_date || q.expected_delivery_date)
                                        .sort((a, b) => {
                                            const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                                            if (typeOrder !== 0) return typeOrder;
                                            return (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                                        });

                                    if (bookedQuotes.length === 0) return null;

                                    // Booked dates are 'date' PG columns → already "YYYY-MM-DD" strings,
                                    // matching the holiday map's key format directly.
                                    const getHolidaysForBookedDate = (dateString) => {
                                        if (!dateString) return [];
                                        return holidayMap.get(dateString.slice(0, 10)) || [];
                                    };

                                    return (
                                        <DropdownSection
                                            title="Booked Dates"
                                            titleStyle="text-zinc-700 text-xs"
                                            icon={Calendar}
                                            iconStyle="text-zinc-800 !w-4 !h-4"
                                            subtext={``}
                                            subtextStyle="text-xs"
                                            defaultOpen={true}
                                        >
                                            <div className="border-gray-100">
                                                <div className="space-y-1.5">
                                                    {bookedQuotes.map(q => {
                                                        const C = QUOTE_COLORS[q.quote_type] ?? {
                                                            bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-400', label: 'text-gray-700', icon: <FiCalendar className="w-3 h-3" />
                                                        };
                                                        const finish = q.expected_finish_date;
                                                        const delivery = q.expected_delivery_date;


                                                        const etaHolidays = getHolidaysForBookedDate(delivery || finish);

                                                        return (
                                                            <div key={q.id} className={`rounded-lg border ${C.border} ${C.bg} px-2.5 py-2 flex items-start gap-2`}>

                                                                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${C.iconBg} text-white`}>
                                                                    {C.icon}
                                                                </div>


                                                                <div className="flex-1 min-w-0">
                                                                    <p className={`text-[11px] font-semibold capitalize leading-none mb-1 flex items-center gap-1.5 ${C.label}`}>
                                                                        {q?.quote_type?.split("_")?.join(" ") || q.quote_type}
                                                                        {q.child_unique_id && (
                                                                            <span className="ml-1 font-normal text-gray-500">· {q.child_unique_id}</span>
                                                                        )}
                                                                        {q?.on_hold && (
                                                                            <span title="This quote is on hold" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-600 text-white leading-none whitespace-nowrap">
                                                                                <PauseCircle className="w-2.5 h-2.5 flex-shrink-0" />
                                                                                ON HOLD
                                                                            </span>
                                                                        )}
                                                                    </p>

                                                                    <div className="flex items-center gap-1 text-[11px] text-gray-600">
                                                                        <FiCalendar className="w-3 h-3 flex-shrink-0 text-gray-400" />
                                                                        <span className="text-gray-400">ETA:</span>
                                                                        <span className="font-medium">{formatDateDriver(delivery || finish)}</span>
                                                                    </div>

                                                                    {etaHolidays.length > 0 && (
                                                                        <p className="text-[11px] mt-1 flex items-center gap-1 text-amber-700 font-semibold" title={etaHolidays.join(', ')}>
                                                                            <PartyPopper className="w-3 h-3 flex-shrink-0" />
                                                                            <span className="truncate">
                                                                                {etaHolidays[0]}{etaHolidays.length > 1 ? ` +${etaHolidays.length - 1}` : ''}
                                                                            </span>
                                                                        </p>
                                                                    )}


                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </DropdownSection>
                                    );
                                })()}

                                <div className="ml-auto w-fit">
                                    <div className="flex flex-wrap items-center justify-end gap-2">
                                        <button
                                            type="button"
                                            disabled={assignment.is_cancelled}
                                            onClick={() => navigate(`/${companySlug}/assignment/${jobId}/conversation`)}
                                            className="relative flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-[0.4rem] text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <FiMessageSquare className="w-4 h-4" />
                                            Open Conversation
                                        </button>
                                        <JobBookedDatesEmailSender
                                            assignment={{ ...assignment, job_quotes: (assignment.job_quotes || []).filter(q => q.id === departmentId) }}
                                            userProfile={userProfile}
                                            company={company}
                                        />
                                    </div>
                                </div>
                            </InfoCard>
                        </div>

                        {/* Job Requirements */}
                        <InfoCard title="Job Requirements" icon={FiTool} color="green">
                            <div className="flex flex-col gap-4">
                                {assignment?.job_type?.toLowerCase()?.includes("design") &&
                                    <DetailRow
                                        label="Required Sections"
                                        value={renderQuoteChips({ ...assignment, job_quotes: assignment.job_quotes?.filter(q => q.id === departmentId) })}
                                        icon={FiCheckCircle}
                                        extraStyle="flex flex-col gap-1"
                                    />
                                }

                                {assignment.job_type !== "Timber" && (
                                    <DetailRow
                                        label="Priority Settings"
                                        value={(
                                            <div className="flex flex-col gap-1 mt-2">
                                                {assignment?.job_type !== "Design" &&
                                                    <CheckboxDisplay label="Urgent Quote" isChecked={assignment.urgent_quote} />
                                                }
                                                {assignment?.job_type !== "Estimation" &&
                                                    <CheckboxDisplay label="Urgent Design" isChecked={assignment.urgent_design} />
                                                }
                                            </div>
                                        )}
                                        icon={FiClock}
                                    />
                                )}
                                <DetailRow
                                    label="Unit Job"
                                    value={assignment?.is_unit ? `Yes (${assignment?.unit_count} Unit${assignment?.unit_count > 1 ? "s" : ""})` : `No`}
                                    icon={SquareStack}
                                />
                                <DetailRow
                                    label={
                                        <div className="flex items-center gap-2">
                                            <span>Site Measure</span>
                                            {isSiteMeasureRequired && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500 text-white font-semibold">
                                                    REQUIRED
                                                </span>
                                            )}
                                        </div>
                                    }
                                    value={(
                                        <div className="flex flex-col gap-1 mt-1">
                                            {assignment?.job_quotes?.some(q => q.id === departmentId && q?.quote_type === "roof" && q?.is_required) && (
                                                <CheckboxDisplay
                                                    label="Roof Measure"
                                                    isChecked={assignment?.site_measure_required && assignment?.site_measure_truss}
                                                />
                                            )}

                                            {assignment?.job_quotes?.some(q => q.id === departmentId && q?.quote_type === "wall" && q?.is_required) && (<CheckboxDisplay
                                                label="Wall Measure"
                                                isChecked={assignment?.site_measure_required && assignment?.site_measure_wall}
                                            />
                                            )}

                                            {assignment?.job_quotes?.some(q => q.id === departmentId && q?.quote_type === "floor" && q?.is_required) && (<CheckboxDisplay
                                                label="Floor Measure"
                                                isChecked={assignment?.site_measure_required && assignment?.site_measure_floor}
                                            />
                                            )}
                                        </div>
                                    )}
                                    icon={PencilRuler}
                                    containerStyle={`relative pl-3 py-1 pr-2 rounded transition-all ${isSiteMeasureRequired
                                        ? "bg-green-50 border border-green-200"
                                        : ""
                                        }`}
                                />
                                <DropdownSection
                                    title="Invoice"
                                    titleStyle="text-zinc-700 text-xs"
                                    icon={Receipt}
                                    iconStyle="text-zinc-800 !w-4 !h-4"
                                    subtext={``}
                                    subtextStyle="text-xs"
                                    defaultOpen={true}
                                >
                                    {(() => {
                                        const jobQuotes = (assignment.job_quotes ?? [])
                                            .filter(q => q.is_required && q.id === departmentId)
                                            .sort((a, b) => {
                                                const typeOrder = (QUOTE_ORDER[a.quote_type] ?? 99) - (QUOTE_ORDER[b.quote_type] ?? 99);
                                                if (typeOrder !== 0) return typeOrder;

                                                const childCompare = (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                                                if (childCompare !== 0) return childCompare;

                                                return (a.delivery_count || 0) - (b.delivery_count || 0);
                                            });

                                        const groupedJobQuotes = getSortedQuoteTypeEntries(jobQuotes);


                                        const getDisplayName = (quote) => quote?.child_unique_id || quote?.quote_type;

                                        if (!jobQuotes.length) {
                                            return (
                                                <div className="text-xs text-gray-400 italic px-1 py-1">
                                                    No invoice items
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="space-y-3">
                                                {groupedJobQuotes.map(group => (
                                                    <DropdownSection
                                                        key={`invoice-group-${group.quoteType}`}
                                                        title={group.label}
                                                        titleStyle="text-zinc-600 text-xs"
                                                        iconStyle="text-zinc-600"
                                                        subtext={`${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                                                        subtextStyle="text-[11px]"
                                                        defaultOpen={false}
                                                    >
                                                        <div className="flex flex-wrap gap-2">
                                                            {group.items.map(quote => {
                                                                const invoiceState = (
                                                                    quote?.xero_invoice_status ||
                                                                    quote?.invoice_status ||
                                                                    quote?.invoice_state ||
                                                                    "not_invoiced"
                                                                )
                                                                    .toString()
                                                                    .toLowerCase()
                                                                    .replaceAll(" ", "_");


                                                                const meta = INVOICE_META[invoiceState] || INVOICE_META.not_invoiced;
                                                                const displayName = getDisplayName(quote);
                                                                const isRedelivery = quote?.delivery_count != null && quote?.delivery_count > 0;

                                                                const icon = getInvoiceSourceIcon(quote, company, 'sm');
                                                                const text = getInvoiceStatusText(quote, company);

                                                                const source = getInvoiceStatusSource(quote, company);
                                                                const canOpenXeroPdf = source === 'xero' && !!quote?.xero_invoice_id;

                                                                return (

                                                                        <button
                                                                            type="button"
                                                                            key={`${quote.id}-inv`}
                                                                            className={`inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${meta.wrap}`}
                                                                            onClick={() => canOpenXeroPdf && openXeroInvoicePdf(quote)}
                                                                            disabled={assignment.is_cancelled || !canOpenXeroPdf}
                                                                            title={canOpenXeroPdf ? 'View Xero invoice PDF' : 'PDF available only for linked Xero invoices'}
                                                                        >
                                                                            <div className={`rounded-md p-1 ${meta.iconWrap}`}>
                                                                                {quote?.quote_type === 'roof' && <Home className="w-3.5 h-3.5" />}
                                                                                {quote?.quote_type === 'wall' && <PanelsTopLeft className="w-3.5 h-3.5" />}
                                                                                {quote?.quote_type === 'floor' && <HiOutlineSquare3Stack3D className="w-3.5 h-3.5" />}
                                                                                {quote?.quote_type === 'timber' && <GiWoodBeam className="w-3.5 h-3.5" />}
                                                                            </div>

                                                                            <span className="text-xs font-semibold text-gray-800 capitalize">
                                                                                {displayName}
                                                                            </span>

                                                                            {isRedelivery && (
                                                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-white/80 border border-gray-200 text-gray-600">
                                                                                    D#{quote.delivery_count}
                                                                                </span>
                                                                            )}
                                                                            <div className={`flex flex-row items-center text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-md border ${meta.badge}`}>
                                                                                {icon}
                                                                                <span className={`ml-1`}>
                                                                                    {text}
                                                                                </span>
                                                                            </div>


                                                                        </button>

                                                                );
                                                            })}
                                                        </div>
                                                    </DropdownSection>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </DropdownSection>
                            </div>
                        </InfoCard>
                    </div>

                    {/* Driver Delivery Section - Only for assigned driver, showing all deliveries */}
                    {hasPermission(userProfile?.role, ["driver"]) && getDriverAssignedDeliveries().length > 0 && (
                        <div className="space-y-4">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 pb-2">
                                    My Deliveries
                                </h2>
                                <span className="text-xs md:text-sm text-gray-500">
                                    {getDriverAssignedDeliveries().length} delivery assignment{getDriverAssignedDeliveries().length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            <div className="space-y-6">
                                {getDriverAssignedDeliveries().map(delivery => {
                                    const quoteType = delivery.quote_type;
                                    const deliveryCount = delivery.delivery_count ?? 0;
                                    const ordinalSuffix = deliveryCount === 0 ? '' : deliveryCount === 1 ? '2nd ' : deliveryCount === 2 ? '3rd ' : `${deliveryCount + 1}th `;

                                    const quote = assignment.job_quotes?.find(q =>
                                        q.quote_type === quoteType &&
                                        (q.delivery_count === deliveryCount || (deliveryCount === 0 && (q.delivery_count === null || q.delivery_count === 0)))
                                    );
                                    const deliveryPhotos = assignment.job_files?.filter(f => {
                                        if (f.file_type !== 'delivery' || f.quote_type !== quoteType) return false;
                                        // Handle backwards compatibility for delivery_count
                                        if (deliveryCount === 0) {
                                            return f.delivery_count === 0 || f.delivery_count === null || f.delivery_count === undefined;
                                        }
                                        return f.delivery_count === deliveryCount;
                                    }) || [];

                                    const statusConfig = {
                                        'not_scheduled': { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' },
                                        'scheduled': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
                                        'in_transit': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
                                        'delivered': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' }
                                    };

                                    const config = statusConfig[delivery?.delivery_status] || statusConfig['not_scheduled'];

                                    return (
                                        <div key={`${quoteType}-${deliveryCount}`} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                            {/* Header */}
                                            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100 text-gray-600">
                                                            {quoteType === 'roof' ? <FiHome className="w-4 h-4" /> :
                                                                quoteType === 'wall' ? <FiLayers className="w-4 h-4" /> :
                                                                    <FiLayers className="w-4 h-4 rotate-90" />}
                                                        </div>
                                                        <div>
                                                            <h3 className="text-sm font-semibold text-gray-900 capitalize">
                                                                {ordinalSuffix}{quoteType} Delivery
                                                            </h3>
                                                            <p className="text-xs text-gray-500">
                                                                {delivery?.delivery_status?.replace('_', ' ').toUpperCase() || 'NOT SCHEDULED'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${delivery?.delivery_status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                                                        delivery?.delivery_status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                                                            delivery?.delivery_status === 'scheduled' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        {delivery?.delivery_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Content */}
                                            <div className="p-4 space-y-4">
                                                {/* Delivery Details */}
                                                <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <FiCalendar className="w-4 h-4 text-gray-400" />
                                                            <h4 className="font-semibold text-gray-800">Schedule</h4>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <DetailRow
                                                                label="Delivery Date"
                                                                value={formatDateDriver(delivery?.delivery_date || quote?.expected_delivery_date)}
                                                            />
                                                            {/* <DetailRow
                                                                label="Delivery Time"
                                                                value={delivery?.delivery_time || "Not specified"}
                                                            /> */}
                                                        </div>
                                                    </div>

                                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <FiUser className="w-4 h-4 text-gray-400" />
                                                            <h4 className="font-semibold text-gray-800">Contact</h4>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <DetailRow
                                                                label="Contact Name"
                                                                value={delivery?.delivery_contact_name || assignment.customer_name}
                                                            />
                                                            <DetailRow
                                                                label="Phone"
                                                                value={delivery?.delivery_contact_phone || assignment.customer_number}
                                                                link={`tel:${delivery?.delivery_contact_phone || assignment.customer_number}`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Delivery Address */}
                                                {(
                                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiMapPin className="w-4 h-4 text-gray-400" />
                                                            <h4 className="font-semibold text-gray-800">Delivery Address</h4>
                                                        </div>
                                                        <p className="text-sm text-gray-700 whitespace-pre">{assignment?.customer_address || "Not specified"}</p>

                                                        <a
                                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment?.customer_address)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 mt-1"
                                                        >
                                                            <FiMapPin className="w-3 h-3" />
                                                            Open in Maps
                                                        </a>

                                                    </div>
                                                )}

                                                {/* Special Instructions */}
                                                {quote?.delivery_note && (
                                                    <div className="bg-white rounded-lg border border-amber-200 p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiAlertCircle className="w-4 h-4 text-amber-600" />
                                                            <h4 className="font-semibold text-amber-900">Delivery Note</h4>
                                                        </div>
                                                        <p className="text-sm text-red-600 font-semibold">{quote.delivery_note}</p>
                                                    </div>
                                                )}

                                                {/* Special Instructions */}
                                                {delivery?.special_instructions && (
                                                    <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <FiAlertCircle className="w-4 h-4 text-amber-600" />
                                                            <h4 className="font-semibold text-amber-900">Delivery Instructions</h4>
                                                        </div>
                                                        <p className="text-sm text-amber-800">{delivery.special_instructions}</p>
                                                    </div>
                                                )}

                                                {/* Action Buttons Based on Delivery Status */}
                                                <div className="space-y-4 pt-4 border-t border-gray-200">
                                                    {(!delivery?.delivery_status || delivery?.delivery_status === 'scheduled') ? (
                                                        // Step 1: Confirm Package Receipt
                                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                                                                    <FiCheckCircle className="w-4 h-4 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="text-sm font-semibold text-gray-900">Step 1: Confirm Package</h4>
                                                                    <p className="text-xs text-gray-600">Confirm that you have received the {quoteType} package</p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => handleConfirmPackage(quoteType, deliveryCount)}
                                                                className="w-full py-2.5 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900 transition-all min-h-[44px]"
                                                            >
                                                                I Have the Package
                                                            </button>
                                                        </div>
                                                    ) : delivery?.delivery_status === 'in_transit' ? (
                                                        // Step 2: Upload Delivery Photos
                                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                                                                    <FiUpload className="w-4 h-4 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="text-sm font-semibold text-gray-900">Step 2: Upload Delivery Photos</h4>
                                                                    <p className="text-xs text-gray-600">Take photos of the delivered {quoteType} package</p>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-4">
                                                                <div>
                                                                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                                                        Upload Photos *
                                                                    </label>
                                                                    <DragDropUploader
                                                                        onFilesChange={handleDeliveryPhotosChange(quoteType, deliveryCount)}
                                                                        clearFiles={clearDeliveryPhotos[`${quoteType}-${deliveryCount}`]}
                                                                        acceptedTypes=".jpg,.jpeg,.png"
                                                                    />
                                                                </div>

                                                                <div>
                                                                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                                                        Delivery Notes (Optional)
                                                                    </label>
                                                                    <textarea
                                                                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm md:text-base"
                                                                        value={deliveryNotes[`${quoteType}-${deliveryCount}`] || ''}
                                                                        onChange={(e) => setDeliveryNotes(prev => ({
                                                                            ...prev,
                                                                            [`${quoteType}-${deliveryCount}`]: e.target.value
                                                                        }))}
                                                                        rows="3"
                                                                        placeholder={`Add any notes about the ${quoteType} delivery...`}
                                                                    />
                                                                </div>

                                                                <button
                                                                    onClick={() => handleUploadDeliveryPhotos(quoteType, deliveryCount)}
                                                                    disabled={isSubmittingDelivery || !uploadedDeliveryPhotos[`${quoteType}-${deliveryCount}`]?.length}
                                                                    className="w-full py-2.5 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[44px]"
                                                                >
                                                                    {isSubmittingDelivery ? (
                                                                        <>
                                                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                            Uploading...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiUpload className="w-4 h-4" />
                                                                            Upload Photos
                                                                        </>
                                                                    )}
                                                                </button>

                                                                {/* Show uploaded photos if any */}
                                                                {deliveryPhotos.length > 0 && (
                                                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                                                        <p className="text-xs md:text-sm font-medium text-gray-700 mb-2">
                                                                            Uploaded Photos ({deliveryPhotos.length})
                                                                        </p>
                                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                                                                            {deliveryPhotos.map((photo, index) => {
                                                                                const { data: { publicUrl } } = supabase.storage
                                                                                    .from('delivery-uploads') // Changed from 'job-files'
                                                                                    .getPublicUrl(photo.filename);

                                                                                return (
                                                                                    <div key={index} className="relative aspect-square bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                                                                        <img
                                                                                            src={publicUrl}
                                                                                            alt={`Delivery photo ${index + 1}`}
                                                                                            className="w-full h-full object-contain p-1"
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>

                                                                        {/* Step 3: End Delivery */}
                                                                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                                                            <div className="flex items-center gap-3 mb-3">
                                                                                <div className="w-7 h-7 bg-emerald-600 rounded flex items-center justify-center flex-shrink-0">
                                                                                    <FiCheckCircle className="w-4 h-4 text-white" />
                                                                                </div>
                                                                                <div>
                                                                                    <h4 className="text-sm font-semibold text-gray-900">Step 3: Complete Delivery</h4>
                                                                                    <p className="text-xs text-gray-600">Finalize the {quoteType} delivery</p>
                                                                                </div>
                                                                            </div>
                                                                            <button
                                                                                onClick={() => handleEndDelivery(quoteType, deliveryCount)}
                                                                                className="w-full py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-all min-h-[44px]"
                                                                            >
                                                                                End Delivery
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : delivery?.delivery_status === 'delivered' ? (
                                                        // Delivery Completed
                                                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="w-8 h-8 bg-emerald-600 rounded flex items-center justify-center flex-shrink-0">
                                                                    <FiCheckCircle className="w-4 h-4 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="text-sm font-semibold text-emerald-900">Delivery Completed</h4>
                                                                    <p className="text-xs text-emerald-700">This {quoteType} delivery has been successfully completed</p>
                                                                </div>
                                                            </div>

                                                            {/* Show delivery photos */}
                                                            {deliveryPhotos.length > 0 && (
                                                                <div className="space-y-3">
                                                                    <p className="text-xs md:text-sm font-medium text-gray-700">
                                                                        Delivery Photos ({deliveryPhotos.length})
                                                                    </p>
                                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                                        {deliveryPhotos.map((photo, index) => {
                                                                            const { data: { publicUrl } } = supabase.storage
                                                                                .from('delivery-uploads')
                                                                                .getPublicUrl(photo.filename);

                                                                            return (
                                                                                <div key={index} className="relative aspect-square">
                                                                                    <img
                                                                                        src={publicUrl}
                                                                                        alt={`Delivery photo ${index + 1}`}
                                                                                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {delivery?.delivered_at && (
                                                                <p className="text-xs text-gray-600 mt-3">
                                                                    Completed on {formatDate(delivery.delivered_at)}
                                                                </p>
                                                            )}

                                                            {delivery?.delivery_notes && (
                                                                <div className="mt-3 p-3 bg-white rounded border border-green-200">
                                                                    <p className="text-xs font-medium text-gray-700 mb-1">Delivery Notes:</p>
                                                                    <p className="text-xs text-gray-600">{delivery.delivery_notes}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Notes Section */}
                    {(assignment.job_notes && assignment.job_notes.length > 0) || hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) ? (
                        <DropdownSection
                            title="Notes"
                            titleStyle="text-zinc-800 text-base"
                            icon={NotebookPen}
                            iconStyle="text-zinc-700 self-center"
                            subtext={`${assignment.job_notes?.length || 0} Note${assignment.job_notes?.length === 1 ? "" : "s"}`}
                            subtextStyle="text-xs font-semibold text-zinc-600"
                            customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                            defaultOpen={true}
                        >
                            <div className="space-y-4">
                                {assignment.job_notes && assignment.job_notes.length > 0 && (
                                    <div className="space-y-2">
                                        {assignment.job_notes.map((note) => (
                                            <div key={note.id} className={`p-3 rounded-lg border relative ${note?.is_important ? "bg-yellow-50 border-yellow-600" : "bg-white border-gray-200"}`}>
                                                {note?.is_important &&
                                                    <div className="absolute top-0 right-0 flex flex-row items-center gap-2 bg-yellow-600 text-white px-2 py-1 rounded-bl-lg">
                                                        <BsExclamationTriangle className="w-4 h-4" />
                                                        <span className="uppercase font-semibold text-[12px]">important</span>
                                                    </div>
                                                }
                                                {editingNoteId === note.id ? (
                                                    <EditNoteForm
                                                        note={note}
                                                        onSave={handleSaveEditNote}
                                                        onCancel={cancelEditNote}
                                                        isJobCancelled={isJobCancelled}
                                                    />
                                                ) : (
                                                    <div className="">
                                                        <p className="text-sm text-gray-700 whitespace-pre-wrap mb-3">
                                                            <span className={`font-semibold py-0.5 px-1 mr-2 rounded text-sm ${getNoteCategoryBadgeClasses(note?.category || DEFAULT_NOTE_CATEGORY)}`}>
                                                                {note?.category || DEFAULT_NOTE_CATEGORY}
                                                            </span>
                                                            {note?.child_id &&
                                                                <span className="font-semibold py-0.5 px-1 mr-2 bg-gray-300 text-zinc-700 rounded text-sm">
                                                                    {note?.child_unique_id}
                                                                </span>
                                                            }
                                                            {note.content}
                                                        </p>
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${note.added_by_role === 'admin' ? 'bg-blue-100 text-blue-800' : note.added_by_role === 'designer' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}>
                                                                    {(note.added_by_name || '?').charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">{note.added_by_name}</p>
                                                                    <p className="text-xs text-gray-500 capitalize">{note.added_by_role}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-right">
                                                                    <p className="text-xs text-gray-500">{formatDate(note.added_at)}</p>
                                                                    {note.is_edited && (
                                                                        <p className="text-xs text-gray-400">Edited {formatDate(note.edited_at)}</p>
                                                                    )}
                                                                </div>

                                                                    <button
                                                                        disabled={isJobCancelled}
                                                                        onClick={() => handleSetImportant(note.id, note.is_important)}
                                                                        className="p-1 ml-2 text-gray-400 hover:text-blue-600 transition-colors"
                                                                        title={note.is_important ? "Remove important flag" : "Set Important"}
                                                                        aria-pressed={!!note.is_important}
                                                                    >
                                                                        <BsExclamationTriangle className="w-4 h-4" />
                                                                    </button>

                                                                {canUserModifyNote(note) && (
                                                                    <div className="flex gap-1">

                                                                            <button
                                                                        disabled={isJobCancelled}
                                                                                onClick={() => startEditNote(note.id)}
                                                                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                                                title="Edit note"
                                                                            >
                                                                                <FiEdit3 className="w-4 h-4" />
                                                                            </button>


                                                                            <button
                                                                        disabled={isJobCancelled}
                                                                                onClick={() => handleDeleteNote(note.id)}
                                                                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                                                title="Delete note"
                                                                            >
                                                                                <FiX className="w-4 h-4" />
                                                                            </button>

                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Add Note Form */}
                                <AddNoteForm onAdd={handleAddNote} isJobCancelled={isJobCancelled} />

                            </div>
                        </DropdownSection>
                    ) : null}

                    {!hasPermission(userProfile?.role, ["driver"]) &&
                        <>
                            {/* Files & Documents */}
                            < div className={`grid gap-4 grid-cols-1`}>










                                {assignment.job_files && assignment.job_files.length > 0 && (
                                    <DropdownSection
                                        title="Files & Documents"
                                        titleStyle="text-zinc-800 text-base"
                                        icon={FiFileText}
                                        iconStyle="text-zinc-700 self-center"
                                        subtext={``}
                                        subtextStyle="text-xs"
                                        customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                                        defaultOpen={true}
                                    >
                                        {/* <InfoCard title="Files & Documents" icon={FiFileText} color="purple"> */}
                                        <div className="space-y-3">
                                            {/* Header: Download All + Create Folder */}
                                            <div className="flex flex-wrap justify-between items-center mb-4 pb-3 border-b border-gray-200 gap-2">
                                                {/* Download All Files button */}
                                                {(() => {
                                                    const realFiles = assignment.job_files.filter(f => f.filename !== '__folder_placeholder__');
                                                    return realFiles.length > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadAllFilesWithFolders(realFiles, `${assignment.unique_id}_${assignment?.customer_address?.length > 0 ? assignment?.customer_address : "all-files"}.zip`)}
                                                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-xs font-medium"
                                                        >
                                                            {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                            {isDownloading ? `Downloading ${realFiles.length} files` : `Download All Files (${realFiles.length})`}
                                                        </button>
                                                    );
                                                })()}

                                                {/* Create Folder button / input */}
                                                {!showFolderInput ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowFolderInput(true)}
                                                        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs font-medium"
                                                    >
                                                        <FiFolderPlus className="w-4 h-4" />
                                                        Create Folder
                                                    </button>
                                                ) : (
                                                    <div className="flex items-center gap-2 flex-1 min-w-0 max-w-xs">
                                                        <input
                                                            type="text"
                                                            value={newFolderName}
                                                            onChange={e => setNewFolderName(e.target.value)}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter') handleCreateCustomFolder();
                                                                if (e.key === 'Escape') { setShowFolderInput(false); setNewFolderName(''); }
                                                            }}
                                                            placeholder="Folder name..."
                                                            autoFocus
                                                            className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none min-w-0"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={handleCreateCustomFolder}
                                                            disabled={isCreatingFolder || !newFolderName.trim()}
                                                            className="px-2 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap flex items-center gap-1"
                                                        >
                                                            {isCreatingFolder ? <Loader className="w-3 h-3 animate-spin" /> : 'Create'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setShowFolderInput(false); setNewFolderName(''); }}
                                                            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                                                        >
                                                            <FiX className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Standard file type accordions */}
                                            {['initial', 'design', 'estimation', 'timber', 'delivery'].map(fileType => {
                                                const filesOfType = assignment.job_files.filter(file => file.file_type === fileType);
                                                if (filesOfType.length === 0) return null;

                                                const isOpen = openFolders[fileType] || false;

                                                return (
                                                    <div key={fileType} className="border border-gray-200 rounded-lg overflow-hidden">
                                                        {/* Folder Header — div wrapper prevents nested-button invalid HTML */}
                                                        <div className="flex items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                                                            {/* Toggle button (flex-1 takes most of the row) */}
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleFolder(fileType)}
                                                                className="flex-1 flex items-center justify-between p-4 text-left"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="text-xl text-gray-500">
                                                                        {isOpen ? <FiFolderMinus /> : <FiFolder />}
                                                                    </div>
                                                                    <div className="text-left">
                                                                        <h4 className="font-semibold text-gray-700 capitalize text-sm">
                                                                            {fileType === "initial" ? "Job" : fileType} Files
                                                                        </h4>
                                                                        <p className="text-xs text-gray-500">
                                                                            {filesOfType.length} file{filesOfType.length !== 1 ? 's' : ''} • {(() => {
                                                                                const quoteTypes = [...new Set(filesOfType.map(f => f.quote_type || 'general'))];
                                                                                return quoteTypes.length > 1 ? `${quoteTypes.length} categories` : '1 category';
                                                                            })()}
                                                                        </p>
                                                                    </div>
                                                                    <div className="w-2.5 h-2.5 rounded-full bg-gray-400"></div>
                                                                </div>
                                                                <div className={`text-gray-400 transition-transform duration-200 mr-2 ${isOpen ? 'rotate-180' : ''}`}>
                                                                    <FiChevronDown className="w-5 h-5" />
                                                                </div>
                                                            </button>

                                                            {/* Download button — sibling, not nested inside toggle */}
                                                            <button
                                                                type="button"
                                                                onClick={() => downloadAllFilesWithFolders(filesOfType, `${assignment.unique_id}_${fileType}_files.zip`)}
                                                                className="p-3 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                                title={`Download all ${fileType} files`}
                                                            >
                                                                {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                            </button>
                                                        </div>

                                                        {/* Folder Content - Expandable */}
                                                        {isOpen && (
                                                            <div className="bg-white border-t border-gray-200">
                                                                <div className="p-4 space-y-3">
                                                                    {/* Group by Quote Type - Nested Accordions */}
                                                                    {['roof', 'wall', 'floor', 'timber', 'general'].map(quoteType => {
                                                                        const quoteFiles = filesOfType.filter(file =>
                                                                            (file.quote_type || 'general') === quoteType
                                                                        );
                                                                        if (quoteFiles.length === 0) return null;

                                                                        const quoteKey = `${fileType}-${quoteType}`;
                                                                        const isQuoteOpen = openQuoteFolders[quoteKey] || false;
                                                                        const uploadKey = `${fileType}-${quoteType}-`;

                                                                        // Only show quote folders if there are multiple quote types OR if it's not general
                                                                        const shouldShowQuoteFolder = quoteType !== 'general' || ['roof', 'wall', 'floor', 'timber'].some(type =>
                                                                            filesOfType.some(f => (f.quote_type || 'general') === type)
                                                                        );

                                                                        if (!shouldShowQuoteFolder) {
                                                                            // Show files directly (no sub-folder) + add file button
                                                                            return (
                                                                                <div key={quoteType} className="space-y-2">
                                                                                    {quoteFiles.map((file, index) => (
                                                                                        <div
                                                                                            key={index}
                                                                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                                                        >
                                                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                                <div className="flex-shrink-0">
                                                                                                    {getFileIcon(file.filename)}
                                                                                                </div>
                                                                                                <div className="min-w-0 flex-1">
                                                                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                                                                        {file.original_name}
                                                                                                    </p>
                                                                                                    <p className="text-xs text-gray-500">
                                                                                                        Uploaded by {file.uploaded_by_name} ({file.uploaded_by_role}) • {formatDate(file.uploaded_at)}
                                                                                                    </p>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="flex gap-2 flex-shrink-0 ml-3">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => seeFile(file, assignment.job_files)}
                                                                                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                                                    title="View file"
                                                                                                >
                                                                                                    <FiEye className="w-4 h-4" />
                                                                                                </button>
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => downloadFile(file)}
                                                                                                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                                                                    title="Download file"
                                                                                                >
                                                                                                    <FiDownload className="w-4 h-4" />
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                    {/* Add a new file to this folder */}
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => triggerFileUpload(fileType, department?.quote_type || null)}
                                                                                        disabled={folderUploading === uploadKey}
                                                                                        onDragOver={e => { e.preventDefault(); setDragOverFolder(uploadKey); }}
                                                                                        onDragLeave={() => setDragOverFolder(null)}
                                                                                        onDrop={async e => { e.preventDefault(); setDragOverFolder(null); for (const file of Array.from(e.dataTransfer.files)) { await uploadSingleFile(file, fileType, null, null, false); } }}
                                                                                        className={`w-full flex items-center justify-center gap-2 p-2.5 border border-dashed rounded-lg text-xs transition-colors disabled:opacity-50 ${dragOverFolder === uploadKey ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-300 text-gray-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50'}`}
                                                                                    >
                                                                                        {folderUploading === uploadKey
                                                                                            ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
                                                                                            : dragOverFolder === uploadKey
                                                                                                ? <><FiUpload className="w-3.5 h-3.5" /> Drop to upload</>
                                                                                                : <><FiPlus className="w-3.5 h-3.5" /> Add a new file</>
                                                                                        }
                                                                                    </button>
                                                                                </div>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <div key={quoteType} className="border border-gray-300 rounded-lg overflow-hidden ml-4">
                                                                                {/* Quote Folder Header — sibling buttons, no nesting */}
                                                                                <div className="flex items-center bg-gray-100 hover:bg-gray-200 transition-colors">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => toggleQuoteFolder(fileType, quoteType)}
                                                                                        className="flex-1 flex items-center justify-between p-3 text-left"
                                                                                    >
                                                                                        <div className="flex items-center gap-2">
                                                                                            <div className={`text-lg ${isQuoteOpen ? 'text-gray-500' :
                                                                                                quoteType === 'roof' ? 'text-blue-400' :
                                                                                                    quoteType === 'wall' ? 'text-red-400' :
                                                                                                        quoteType === 'floor' ? 'text-green-400' :
                                                                                                            quoteType === 'timber' ? 'text-amber-400' :
                                                                                                                'text-gray-400'
                                                                                                }`}>
                                                                                                {isQuoteOpen ? <FiFolderMinus /> : <FiFolder />}
                                                                                            </div>
                                                                                            <div className="text-left">
                                                                                                <span className="text-sm font-semibold text-gray-700 capitalize">
                                                                                                    {quoteType === 'general' ? 'General Files' : `${quoteType} Files`}
                                                                                                </span>
                                                                                                <p className="text-xs text-gray-500">
                                                                                                    {quoteFiles.length} file{quoteFiles.length !== 1 ? 's' : ''}
                                                                                                </p>
                                                                                            </div>
                                                                                            <div className={`w-2 h-2 rounded-full ${quoteType === 'roof' ? 'bg-blue-400' :
                                                                                                quoteType === 'wall' ? 'bg-red-400' :
                                                                                                    quoteType === 'floor' ? 'bg-green-400' :
                                                                                                        quoteType === 'timber' ? 'bg-amber-400' :
                                                                                                            'bg-gray-400'
                                                                                                }`}></div>
                                                                                        </div>
                                                                                        <div className={`text-gray-400 transition-transform duration-200 mr-1 ${isQuoteOpen ? 'rotate-180' : ''}`}>
                                                                                            <FiChevronDown className="w-4 h-4" />
                                                                                        </div>
                                                                                    </button>

                                                                                    {/* Download button — sibling */}
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => downloadAllFilesWithFolders(quoteFiles, `${assignment.unique_id}_${fileType}_${quoteType}_files.zip`)}
                                                                                        className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                                                        title={`Download all ${quoteType} files`}
                                                                                    >
                                                                                        {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                                                    </button>
                                                                                </div>

                                                                                {/* Quote Folder Content */}
                                                                                {isQuoteOpen && (
                                                                                    <div className="bg-white border-t border-gray-300">
                                                                                        <div className="p-3 space-y-2">
                                                                                            {quoteFiles.map((file, index) => (
                                                                                                <div
                                                                                                    key={index}
                                                                                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                                                                >
                                                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                                        <div className="flex-shrink-0">
                                                                                                            {getFileIcon(file.filename)}
                                                                                                        </div>
                                                                                                        <div className="min-w-0 flex-1">
                                                                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                                                                {file.original_name}
                                                                                                            </p>
                                                                                                            <p className="text-xs text-gray-500">
                                                                                                                Uploaded by {file.uploaded_by_name} ({file.uploaded_by_role}) • {formatDate(file.uploaded_at)}
                                                                                                            </p>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    <div className="flex gap-2 flex-shrink-0 ml-3">
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            onClick={() => seeFile(file, assignment.job_files)}
                                                                                                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                                                            title="View file"
                                                                                                        >
                                                                                                            <FiEye className="w-4 h-4" />
                                                                                                        </button>
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            onClick={() => downloadFile(file)}
                                                                                                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                                                                            title="Download file"
                                                                                                        >
                                                                                                            <FiDownload className="w-4 h-4" />
                                                                                                        </button>
                                                                                                    </div>
                                                                                                </div>
                                                                                            ))}

                                                                                            {/* Add a new file to this department */}
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => triggerFileUpload(fileType, quoteType)}
                                                                                                disabled={folderUploading === uploadKey}
                                                                                                onDragOver={e => {
                                                                                                    if (isTouchDevice) return;
                                                                                                    e.preventDefault();
                                                                                                    setDragOverFolder(uploadKey);
                                                                                                }}
                                                                                                onDragLeave={() => setDragOverFolder(null)}
                                                                                                onDrop={async e => {
                                                                                                    if (isTouchDevice) return;
                                                                                                    e.preventDefault();
                                                                                                    setDragOverFolder(null);
                                                                                                    for (const file of Array.from(e.dataTransfer.files)) {
                                                                                                        await uploadSingleFile(file, fileType, quoteType, null, false);
                                                                                                    }
                                                                                                }}
                                                                                                className={`w-full flex items-center justify-center gap-2 p-2.5 border border-dashed rounded-lg text-xs transition-colors disabled:opacity-50 ${dragOverFolder === uploadKey ? 'border-blue-400 bg-blue-50 text-blue-600' : 'border-gray-300 text-gray-500 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50'}`}
                                                                                            >
                                                                                                {folderUploading === uploadKey
                                                                                                    ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
                                                                                                    : dragOverFolder === uploadKey
                                                                                                        ? <><FiUpload className="w-3.5 h-3.5" /> Drop to upload</>
                                                                                                        : <><FiPlus className="w-3.5 h-3.5" /> Add a new file</>
                                                                                                }
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {/* Custom folder accordions */}
                                            {(() => {
                                                const customFiles = assignment.job_files.filter(f => f.file_type === 'custom');
                                                const folderNames = [...new Set(customFiles.map(f => f.description).filter(Boolean))];
                                                return folderNames.map(folderName => {
                                                    const folderFiles = customFiles.filter(f => f.description === folderName && f.filename !== '__folder_placeholder__');
                                                    const isOpen = openFolders[`custom-${folderName}`] || false;
                                                    const uploadKey = `custom-${folderName}-`;

                                                    const restrictedFolders = ["Quotes & Invoices", "Layouts & Comps"];

                                                    const isEstimatorRestricted =
                                                        userProfile?.role === "estimator" &&
                                                        restrictedFolders.includes(folderName);
                                                    return (
                                                        <div key={`custom-${folderName}`} className="border border-indigo-200 rounded-lg overflow-hidden">
                                                            {/* Custom Folder Header */}
                                                            <div className="flex items-center bg-indigo-50 hover:bg-indigo-100 transition-colors">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleFolder(`custom-${folderName}`)}
                                                                    className="flex-1 flex items-center justify-between p-4 text-left"
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="text-xl text-indigo-500">
                                                                            {isOpen ? <FiFolderMinus /> : <FiFolder />}
                                                                        </div>
                                                                        <div className="text-left">
                                                                            <h4 className="font-semibold text-indigo-700 text-sm">{folderName}</h4>
                                                                            <p className="text-xs text-indigo-400">
                                                                                {folderFiles.length} file{folderFiles.length !== 1 ? 's' : ''} • Custom folder
                                                                            </p>
                                                                        </div>
                                                                        <div className="w-2.5 h-2.5 rounded-full bg-indigo-400"></div>
                                                                    </div>
                                                                    <div className={`text-indigo-400 transition-transform duration-200 mr-2 ${isOpen ? 'rotate-180' : ''}`}>
                                                                        <FiChevronDown className="w-5 h-5" />
                                                                    </div>
                                                                </button>

                                                                {folderFiles.length > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => downloadAllFilesWithFolders(folderFiles, `${assignment.unique_id}_${folderName}_files.zip`)}
                                                                        className="p-3 text-indigo-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                                                                        title={`Download all files in ${folderName}`}
                                                                    >
                                                                        {isDownloading ? <Loader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {/* Custom Folder Content */}
                                                            {isOpen && (
                                                                <div className="bg-white border-t border-indigo-200">
                                                                    <div className="p-4 space-y-2">
                                                                        {folderFiles.length === 0 ? (
                                                                            <p className="text-xs text-gray-400 text-center py-2">No files yet — upload one below</p>
                                                                        ) : (
                                                                            folderFiles.map((file, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                                                >
                                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                                        <div className="flex-shrink-0">
                                                                                            {getFileIcon(file.filename)}
                                                                                        </div>
                                                                                        <div className="min-w-0 flex-1">
                                                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                                                {file.original_name}
                                                                                            </p>
                                                                                            <p className="text-xs text-gray-500">
                                                                                                Uploaded by {file.uploaded_by_name} ({file.uploaded_by_role}) • {formatDate(file.uploaded_at)}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex gap-2 flex-shrink-0 ml-3">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => seeFile(file, folderFiles)}
                                                                                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                                            title="View file"
                                                                                        >
                                                                                            <FiEye className="w-4 h-4" />
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => downloadFile(file)}
                                                                                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                                                            title="Download file"
                                                                                        >
                                                                                            <FiDownload className="w-4 h-4" />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        )}
                                                                        {isEstimatorRestricted ? (
                                                                            <div className="w-full flex items-center justify-center p-2.5 rounded-lg text-xs border border-dashed border-gray-300 text-gray-400 bg-gray-50">
                                                                                Not enough permission to add files
                                                                            </div>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => triggerFileUpload('custom', null, folderName, true)}
                                                                                disabled={folderUploading === uploadKey}
                                                                                onDragOver={e => { e.preventDefault(); setDragOverFolder(uploadKey); }}
                                                                                onDragLeave={() => setDragOverFolder(null)}
                                                                                onDrop={async e => {
                                                                                    e.preventDefault();
                                                                                    setDragOverFolder(null);
                                                                                    for (const file of Array.from(e.dataTransfer.files)) {
                                                                                        await uploadSingleFile(file, 'custom', null, folderName, true);
                                                                                    }
                                                                                }}
                                                                                className={`w-full flex items-center justify-center gap-2 p-2.5 border border-dashed rounded-lg text-xs transition-colors disabled:opacity-50 ${dragOverFolder === uploadKey
                                                                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                                                                    : 'border-indigo-300 text-indigo-500 hover:text-indigo-700 hover:border-indigo-500 hover:bg-indigo-50'
                                                                                    }`}
                                                                            >
                                                                                {folderUploading === uploadKey
                                                                                    ? <><Loader className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
                                                                                    : dragOverFolder === uploadKey
                                                                                        ? <><FiUpload className="w-3.5 h-3.5" /> Drop to upload</>
                                                                                        : <><FiPlus className="w-3.5 h-3.5" /> Add a new file</>
                                                                                }
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                        {/* </InfoCard> */}
                                    </DropdownSection>
                                )}

                            </div>
                        </>

                    }

                    <DropdownSection
                        title="Pricing"
                        titleStyle="text-zinc-800 text-base"
                        icon={FiDollarSign}
                        iconStyle="text-zinc-700 self-center"
                        subtext="Estimator prices and designer department prices"
                        subtextStyle="text-xs"
                        customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                        defaultOpen={true}
                    >
                        <PricingReviewDropdown
                            assignment={assignment}
                            company={company}
                            userProfile={userProfile}
                            onSaved={fetchAssignment}
                            companySlug={companySlug}
                        />
                    </DropdownSection>

                    {/* Assigned Users Section */}
                    <DropdownSection
                        title="Assigned Users"
                        titleStyle="text-zinc-800 text-base"
                        icon={Users}
                        iconStyle="text-zinc-700 self-center"
                        subtextStyle="text-xs font-semibold text-zinc-500"
                        customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                        defaultOpen={true}
                    >
                        <div className="flex flex-col gap-3">
                            {/* 1. Job Creator Information */}
                            <DropdownSection title="Job Creator" icon={FiUser} titleStyle="text-zinc-700 text-sm" iconStyle="text-zinc-800" defaultOpen={false}>
                                <DetailRow
                                    label="Created By"
                                    value={assignment.created_by_name || "Unknown"}
                                    icon={FiUser}
                                />
                                <DetailRow
                                    label="Role"
                                    value="Admin"
                                    icon={FiTool}
                                />
                                <DetailRow
                                    label="Created On"
                                    value={formatDate(assignment.created_at)}
                                    icon={FiCalendar}
                                />
                            </DropdownSection>

                            {/* 3. Assigned Estimator */}
                            {(assignment.job_type === 'Estimation' || assignment.job_type === 'Design & Estimation') && (
                                <DropdownSection title="Assigned Estimator" icon={FiDollarSign} titleStyle="text-zinc-700 text-sm" iconStyle="text-zinc-800" defaultOpen={false}>
                                    <div className="space-y-4">
                                        {assignment.job_estimators?.user_id ? (
                                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                <div className="flex items-center justify-between mb-2 gap-2">
                                                    <span className="text-xs font-medium text-gray-700">Estimator</span>
                                                    {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                        <button
                                                            onClick={() => handleRemoveEstimator()}
                                                            className="text-xs text-red-600 hover:text-red-800 font-medium min-h-[44px] px-2"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                                                        {assignment.job_estimators?.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <span className="text-xs md:text-sm font-medium text-gray-900">
                                                            {assignment.job_estimators?.full_name?.trim()
                                                                ? assignment.job_estimators.full_name
                                                                : assignment.job_estimators?.name}
                                                        </span>
                                                        {assignment.job_estimators?.is_completed && (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <FiCheckCircle className="w-3 h-3 text-green-500" />
                                                                <span className="text-xs text-green-600">Completed</span>
                                                            </div>
                                                        )}
                                                        {assignment.job_estimators?.is_completed && (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <span className="text-xs text-green-600">Completed by {assignment.job_estimators?.completed_by_name || assignment.job_estimators?.name}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <DetailRow
                                                    label="Assigned On"
                                                    value={formatDate(assignment.job_estimators?.assigned_at)}
                                                    icon={FiCalendar}
                                                />
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <p className="text-xs md:text-sm text-gray-600">No estimator assigned</p>

                                                {(hasPermission(userProfile?.role, ["super_admin", "admin"]) || checkPermissions(userProfile?.permissions, ["ASSIGN_ESTIMATORS"])) && (
                                                    <button
                                                        onClick={() => openAssignEstimatorModal()}
                                                        className="w-full py-2 px-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium min-h-[44px]"
                                                    >
                                                        Assign Estimator
                                                    </button>
                                                )}

                                                {hasPermission(userProfile?.role, ["estimator", "designer"]) && (
                                                    <button
                                                        onClick={() => handleSelfAssignEstimator()}
                                                        className="w-full py-2 px-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium min-h-[44px]"
                                                    >
                                                        Assign Self as Estimator
                                                    </button>
                                                )}

                                                {hasPermission(userProfile?.role, ["designer"]) && (
                                                    <p className="text-xs text-gray-500 italic">Contact admin to assign estimator</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </DropdownSection>
                            )}

                            {/* 2. Assigned Designer */}
                            {(assignment.job_type === 'Design' || assignment.job_type === 'Design & Estimation') && (
                                <DropdownSection title="Assigned Designer" icon={FiEdit3} titleStyle="text-zinc-700 text-sm" iconStyle="text-zinc-800" defaultOpen={false}>
                                    {(() => {
                                        const designerQuotes = [department]
                                        const n = designerQuotes.length;
                                        const gridCols =
                                            n <= 1 ? 'grid-cols-1' :
                                                n === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                                                    n === 3 ? 'grid-cols-1 sm:grid-cols-3' :
                                                        'grid-cols-2 lg:grid-cols-4';
                                        return (
                                            <div className={`grid gap-2 ${gridCols}`}>
                                                {designerQuotes.filter(quotes => quotes.id === department.id).map(dep => {
                                                    const quoteType = dep.quote_type;
                                                    const childName = dep.child_unique_id;

                                                    // New jobs: match by child_job_id (UUID). Old jobs: match by quote_type with no child link.
                                                    const depDesigners = (assignment.job_designers ?? []).filter(d => d.child_job_id === dep.id)

                                                    const C = {
                                                        roof: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-600', avatarBg: 'bg-gray-600', divider: 'divide-gray-100', icon: <FiHome className="w-3 h-3" /> },
                                                        wall: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-600', avatarBg: 'bg-gray-600', divider: 'divide-gray-100', icon: <FiLayers className="w-3 h-3" /> },
                                                        floor: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-600', avatarBg: 'bg-gray-600', divider: 'divide-gray-100', icon: <FiLayers className="w-3 h-3 rotate-90" /> },
                                                        timber: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-600', avatarBg: 'bg-gray-600', divider: 'divide-gray-100', icon: <FiLayers className="w-3 h-3" /> },
                                                    }[quoteType] ?? { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-400', avatarBg: 'bg-gray-400', divider: 'divide-gray-100', icon: <FiUser className="w-3 h-3" /> };

                                                    const canAssign = hasPermission(userProfile?.role, ["super_admin", "admin"]) || checkPermissions(userProfile?.permissions, ["ASSIGN_DESIGNERS"]);
                                                    const isSelfAssigned = (assignment.job_designers ?? []).some(d =>
                                                        d.user_id === userProfile.id &&
                                                        (childName ? d.child_job_id === dep.id : d.quote_type === quoteType)
                                                    );
                                                    const canSelfAssign = (hasPermission(userProfile?.role, ["designer"]) || checkPermissions(userProfile?.permissions, ["ASSIGN_DESIGNER_SELF"])) && !isSelfAssigned;

                                                    return (
                                                        <div key={dep.id || quoteType} className={`rounded-lg border ${C.border} overflow-hidden`}>

                                                            {/* ── Quote header ── */}
                                                            <div className={`flex items-center justify-between px-3 py-2 ${C.bg} border-b ${C.border}`}>
                                                                <div className="flex items-center gap-2">
                                                                    <div className={`w-5 h-5 rounded flex items-center justify-center ${C.iconBg} text-white flex-shrink-0`}>
                                                                        {C.icon}
                                                                    </div>
                                                                    <span className="text-xs font-semibold text-gray-700 capitalize">
                                                                        {quoteType}
                                                                        {childName && <span className="ml-1.5 font-normal text-gray-400">· {childName}</span>}
                                                                    </span>
                                                                    {depDesigners.length > 0 && (
                                                                        <span className="text-[10px] text-gray-400 font-medium">
                                                                            {depDesigners.length} {depDesigners.length === 1 ? 'designer' : 'designers'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {canAssign && depDesigners.length <= 0 && (
                                                                        <button
                                                                            onClick={() => openAssignDesignerModal(dep.quote_type, dep.id, dep?.child_unique_id || null)}
                                                                            className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                                                                        >
                                                                            <FiEdit3 className="w-3 h-3" />
                                                                            Assign
                                                                        </button>
                                                                    )}
                                                                    {canSelfAssign && (
                                                                        <button
                                                                            onClick={() => handleSelfAssignDesigner(dep.quote_type, dep.id, dep?.child_unique_id || null)}
                                                                            className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                                                                        >
                                                                            + Self
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* ── Designer rows ── */}
                                                            {depDesigners.length === 0 ? (
                                                                <div className="px-3 py-2.5 bg-white">
                                                                    <p className="text-xs text-gray-400">No designer assigned</p>
                                                                </div>
                                                            ) : (
                                                                <div className={`divide-y ${C.divider} bg-white`}>
                                                                    {depDesigners.map(designer => (
                                                                        <div key={designer.id} className="flex items-center justify-between px-3 py-2.5 gap-2">
                                                                            <div className="flex items-center gap-2 min-w-0">
                                                                                <div className={`w-7 h-7 ${C.avatarBg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                                                                                    {designer.name?.charAt(0) || '?'}
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <p className="text-xs font-semibold text-gray-800 truncate leading-tight">
                                                                                        {designer.name}
                                                                                    </p>
                                                                                    {designer.is_completed && (
                                                                                        <p className="text-[10px] text-gray-400 leading-tight truncate">
                                                                                            by {designer.completed_by_name || designer.name}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${designer.is_completed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                                                    {designer.is_completed ? 'finished' : 'in progress'}
                                                                                </span>
                                                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                    <button
                                                                                        onClick={() => handleRemoveDesigner(dep.quote_type, designer.user_id, dep?.id || null)}
                                                                                        className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                                                        title="Remove designer"
                                                                                    >
                                                                                        <FiX className="w-3.5 h-3.5" />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}
                                </DropdownSection>
                            )}




                            {/* 4. Assigned Drivers — per child quote, supports multi-quote system */}
                            <DropdownSection title="Assigned Drivers" icon={FiTruck} titleStyle="text-zinc-700 text-sm" iconStyle="text-zinc-800" defaultOpen={false}>
                                {(() => {
                                    const driverQuotes = [department]
                                    const n = driverQuotes.length;
                                    const gridCols =
                                        n <= 1 ? 'grid-cols-1' :
                                            n === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                                                n === 3 ? 'grid-cols-1 sm:grid-cols-3' :
                                                    'grid-cols-2 lg:grid-cols-4';
                                    return (
                                        <div className={`grid gap-2 ${gridCols}`}>
                                            {driverQuotes.map(quote => {
                                                const quoteType = quote.quote_type;
                                                const childName = quote.child_unique_id;
                                                const childId = quote.child_id;
                                                const quoteId = quote.id;

                                                // Colour config per type
                                                const C = {
                                                    roof: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-600', divider: 'divide-gray-100', icon: <FiHome className="w-3 h-3" /> },
                                                    wall: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-600', divider: 'divide-gray-100', icon: <FiLayers className="w-3 h-3" /> },
                                                    floor: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-600', divider: 'divide-gray-100', icon: <FiLayers className="w-3 h-3 rotate-90" /> },
                                                    timber: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-600', divider: 'divide-gray-100', icon: <FiLayers className="w-3 h-3" /> },
                                                }[quoteType] ?? { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-400', divider: 'divide-gray-100', icon: <FiTruck className="w-3 h-3" /> };

                                                // Match deliveries to this specific child quote.
                                                // job_deliveries.child_unique_id stores the job_quotes.id UUID (not the display string).
                                                // — new deliveries: d.child_unique_id (UUID) matches quoteId (job_quotes.id)
                                                // — old deliveries (no child_unique_id on delivery): show only for quotes without a child display name
                                                const deliveries = (assignment.job_deliveries ?? [])
                                                    .filter(d => {
                                                        if (d.quote_type !== quoteType) return false;
                                                        if (d.child_unique_id) return d.child_unique_id === quoteId; // UUID match
                                                        return !childName; // old jobs: no UUID stored
                                                    })
                                                    .sort((a, b) => (a.delivery_count ?? 0) - (b.delivery_count ?? 0));

                                                const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th'];
                                                const STATUS_STYLES = {
                                                    delivered: 'bg-emerald-100 text-emerald-700',
                                                    in_transit: 'bg-blue-100 text-blue-700',
                                                    scheduled: 'bg-amber-100 text-amber-700',
                                                };

                                                return (
                                                    <div key={quoteId || quoteType} className={`rounded-lg border ${C.border} overflow-hidden`}>

                                                        {/* ── Quote header ── */}
                                                        <div className={`flex items-center justify-between px-3 py-2 ${C.bg} border-b ${C.border}`}>
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-5 h-5 rounded flex items-center justify-center ${C.iconBg} text-white flex-shrink-0`}>
                                                                    {C.icon}
                                                                </div>
                                                                <span className="text-xs font-semibold text-gray-700 capitalize">
                                                                    {quoteType}
                                                                    {childName && (
                                                                        <span className="ml-1.5 font-normal text-gray-400">· {childName}</span>
                                                                    )}
                                                                </span>
                                                                {deliveries.length > 0 && (
                                                                    <span className="text-[10px] text-gray-400 font-medium">
                                                                        {deliveries.length} {deliveries.length === 1 ? 'delivery' : 'deliveries'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {/* Assign button shown in header only when there are no deliveries yet */}
                                                            {hasPermission(userProfile?.role, ["super_admin", "admin"]) && deliveries.length === 0 && (
                                                                <button
                                                                    onClick={() => openAssignDriverModal(quoteType, 0, quoteId, childName)}
                                                                    className="flex items-center gap-1 text-[11px] font-semibold text-teal-600 hover:text-teal-800 transition-colors"
                                                                >
                                                                    <FiTruck className="w-3 h-3" /> Assign
                                                                </button>
                                                            )}
                                                        </div>

                                                        {/* ── Delivery rows ── */}
                                                        {deliveries.length === 0 ? (
                                                            <div className="px-3 py-2.5 bg-white">
                                                                <p className="text-xs text-gray-400">No deliveries scheduled yet</p>
                                                            </div>
                                                        ) : (
                                                            <div className={`divide-y ${C.divider} bg-white`}>
                                                                {deliveries.map(delivery => {
                                                                    const deliveryCount = delivery.delivery_count ?? 0;
                                                                    const ordinal = ORDINALS[deliveryCount] ?? `${deliveryCount + 1}th`;
                                                                    const isCurrentDriver = userProfile?.role === 'driver' && delivery?.assigned_driver_id === userProfile.id;
                                                                    const statusStyle = STATUS_STYLES[delivery?.delivery_status] ?? 'bg-gray-100 text-gray-500';

                                                                    // Find matching job_quote row for units/notes
                                                                    const quoteCurrent = deliveryCount === 0 ? quote
                                                                        : assignment.job_quotes?.find(q =>
                                                                            q.quote_type === quoteType &&
                                                                            (q.delivery_count ?? 0) === deliveryCount &&
                                                                            (!childName || q.child_unique_id === childName)
                                                                        );

                                                                    return (
                                                                        <div key={`${quoteType}-${deliveryCount}`} className="px-3 py-2.5">
                                                                            {/* Delivery label — only shown when multiple deliveries */}
                                                                            {deliveries.length > 1 && (
                                                                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                                                                                    {ordinal} Delivery
                                                                                </p>
                                                                            )}

                                                                            {delivery?.assigned_driver_id ? (
                                                                                <>
                                                                                    {/* Driver row */}
                                                                                    <div className="flex items-center justify-between gap-2">
                                                                                        <div className="flex items-center gap-2 min-w-0">
                                                                                            <div className="w-7 h-7 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                                                                {delivery.assigned_driver_name?.charAt(0) || 'D'}
                                                                                            </div>
                                                                                            <div className="min-w-0">
                                                                                                <p className="text-xs font-semibold text-gray-800 truncate leading-tight">
                                                                                                    {delivery.assigned_driver_name || 'Unknown'}
                                                                                                    {isCurrentDriver && <span className="ml-1 text-teal-500 font-normal">(You)</span>}
                                                                                                </p>
                                                                                                {delivery?.delivery_date ? (
                                                                                                    <p className="text-[10px] text-gray-400 truncate leading-tight">
                                                                                                        {formatDate(delivery.delivery_date)}{delivery.delivery_time && ` · ${delivery.delivery_time}`}
                                                                                                    </p>
                                                                                                ) : (
                                                                                                    <p className="text-[10px] text-gray-400 leading-tight">
                                                                                                        Assigned {formatDate(delivery.assigned_at)}
                                                                                                    </p>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${statusStyle}`}>
                                                                                                {delivery?.delivery_status?.replace(/_/g, ' ') || 'pending'}
                                                                                            </span>
                                                                                            {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                                <button
                                                                                                    onClick={() => handleRemoveDriver(quoteType, deliveryCount, quoteId)}
                                                                                                    className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                                                                    title="Remove driver"
                                                                                                >
                                                                                                    <FiX className="w-3.5 h-3.5" />
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>

                                                                                    {/* Units */}
                                                                                    {quoteCurrent?.selected_units?.length > 0 && (
                                                                                        <div className="flex flex-wrap gap-1 mt-1.5 ml-9">
                                                                                            {quoteCurrent.selected_units.map(u => (
                                                                                                <span key={u} className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700">
                                                                                                    Unit {u}
                                                                                                </span>
                                                                                            ))}
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Note */}
                                                                                    {quoteCurrent?.delivery_note?.length > 0 && (
                                                                                        <div className="flex items-start gap-1 mt-1.5 ml-9">
                                                                                            <FiPenTool className="w-3 h-3 text-red-400 flex-shrink-0 mt-px rotate-180" />
                                                                                            <p className="text-[10px] text-red-600 leading-tight">{quoteCurrent.delivery_note}</p>
                                                                                        </div>
                                                                                    )}
                                                                                </>
                                                                            ) : (
                                                                                /* No driver yet */
                                                                                <div className="flex items-center justify-between">
                                                                                    <p className="text-xs text-gray-400">No driver assigned</p>
                                                                                    {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                        <button
                                                                                            onClick={() => openAssignDriverModal(quoteType, deliveryCount, quoteId, childName)}
                                                                                            className="flex items-center gap-1 text-[11px] font-semibold text-teal-600 hover:text-teal-800 transition-colors"
                                                                                        >
                                                                                            <FiTruck className="w-3 h-3" /> Assign
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </DropdownSection>

                        </div>


                    </DropdownSection>


                    {!hasPermission(userProfile?.role, ["driver"]) &&
                        <DropdownSection
                            title="Departments"
                            titleStyle="text-zinc-800 text-base"
                            icon={Info}
                            iconStyle="text-zinc-700 self-center"
                            subtext={``}
                            subtextStyle="text-xs font-semibold text-zinc-500"
                            customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                            defaultOpen={true}
                        >
                            <>
                                {/* Job Specifications */}
                                {assignment.job_quotes?.some(q => q.id === departmentId && q.is_required && ['roof', 'wall', 'floor'].includes(q.quote_type)) && (
                                    <div className="space-y-4">

                                        {/* Roof Specifications */}
                                        {assignment.job_quotes?.find(q => q.id === departmentId && q.quote_type === 'roof' && q.is_required) && (
                                            <DropdownSection
                                                title={<span className="inline-flex items-center gap-1.5">Roof Department{assignment.job_quotes?.find(q => q.id === departmentId && q.quote_type === 'roof' && q.is_required)?.on_hold && <span title="This quote is on hold" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-600 text-white leading-none whitespace-nowrap"><PauseCircle className="w-2.5 h-2.5" />ON HOLD</span>}</span>}
                                                titleStyle="text-zinc-700 text-sm"
                                                icon={FiHome}
                                                iconStyle="text-zinc-800"
                                                subtext={``}
                                                subtextStyle="text-xs "
                                                defaultOpen={false}
                                            >
                                                <DetailRow
                                                    label="Roof Type"
                                                    value={assignment.roof_type}
                                                />
                                                <DetailRow
                                                    label="Roof Truss Centres"
                                                    value={assignment.roof_truss_centres}
                                                />
                                                <DetailRow
                                                    label="Roof Measure"
                                                    value={
                                                        <CheckboxDisplay
                                                            label={`${assignment.site_measure_truss ? "Roof measure required" : "Roof measure not required"}`}
                                                            isChecked={assignment.site_measure_truss}
                                                        />
                                                    }

                                                />
                                            </DropdownSection>
                                        )}

                                        {/* Wall Specifications */}
                                        {assignment.job_quotes?.find(q => q.id === departmentId && q.quote_type === 'wall' && q.is_required) && (
                                            <DropdownSection
                                                title={<span className="inline-flex items-center gap-1.5">Wall Department{assignment.job_quotes?.find(q => q.id === departmentId && q.quote_type === 'wall' && q.is_required)?.on_hold && <span title="This quote is on hold" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-600 text-white leading-none whitespace-nowrap"><PauseCircle className="w-2.5 h-2.5" />ON HOLD</span>}</span>}
                                                titleStyle="text-zinc-700 text-sm"
                                                icon={PanelsTopLeft}
                                                iconStyle="text-zinc-800"
                                                subtext={``}
                                                subtextStyle="text-xs "
                                                defaultOpen={false}
                                            >

                                                <DetailRow
                                                    label="Building Type"
                                                    value={assignment.is_single_storey ? "Single Storey" : "Double Storey"}
                                                />

                                                {assignment.is_single_storey ? (
                                                    // Single Storey - Get from job_floor_specs where floor_level = 'single_storey' or 'ground_floor'
                                                    assignment.job_floor_specs?.filter(spec =>
                                                        spec.floor_level === 'single' || spec.floor_level === 'ground'
                                                    ).map((spec, index) => (
                                                        <div key={index} className="space-y-2 border-l-4 border-gray-200 pl-4">
                                                            <p className="text-sm font-medium text-gray-900">Single Storey Details</p>
                                                            <DetailRow
                                                                label="Load Bearing Wall Stud Sizes"
                                                                value={spec.load_bearing_wall_stud_sizes}
                                                            />
                                                            <DetailRow
                                                                label="Non Load Bearing Wall Stud Sizes"
                                                                value={spec.non_load_bearing_wall_stud_sizes}
                                                            />
                                                            <DetailRow
                                                                label="Non Load Bearing Wall Stud Centres"
                                                                value={spec.non_load_bearing_wall_stud_centres}
                                                            />
                                                            <DetailRow
                                                                label="Wall Height"
                                                                value={spec.wall_height}
                                                            />
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="space-y-4">
                                                        {/* Ground Floor */}
                                                        {assignment.job_floor_specs?.filter(spec => spec.floor_level === 'ground').map((spec, index) => (
                                                            <div key={index} className="space-y-2 border-l-4 border-blue-200 pl-4">
                                                                <p className="text-sm font-medium text-blue-900">Ground Floor</p>
                                                                <DetailRow
                                                                    label="Load Bearing Wall Stud Sizes"
                                                                    value={spec.load_bearing_wall_stud_sizes}
                                                                />
                                                                <DetailRow
                                                                    label="Non Load Bearing Wall Stud Sizes"
                                                                    value={spec.non_load_bearing_wall_stud_sizes}
                                                                />
                                                                <DetailRow
                                                                    label="Non Load Bearing Wall Stud Centres"
                                                                    value={spec.non_load_bearing_wall_stud_centres}
                                                                />
                                                                <DetailRow
                                                                    label="Wall Height"
                                                                    value={spec.wall_height}
                                                                />
                                                            </div>
                                                        ))}

                                                        {/* First Floor */}
                                                        {assignment.job_floor_specs?.filter(spec => spec.floor_level === 'first').map((spec, index) => (
                                                            <div key={index} className="space-y-2 border-l-4 border-green-200 pl-4">
                                                                <p className="text-sm font-medium text-green-900">First Floor</p>
                                                                <DetailRow
                                                                    label="Load Bearing Wall Stud Sizes"
                                                                    value={spec.load_bearing_wall_stud_sizes}
                                                                />
                                                                <DetailRow
                                                                    label="Non Load Bearing Wall Stud Sizes"
                                                                    value={spec.non_load_bearing_wall_stud_sizes}
                                                                />
                                                                <DetailRow
                                                                    label="Non Load Bearing Wall Stud Centres"
                                                                    value={spec.non_load_bearing_wall_stud_centres}
                                                                />
                                                                <DetailRow
                                                                    label="Wall Height"
                                                                    value={spec.wall_height}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                            </DropdownSection>
                                        )}

                                        {/* Floor Specifications */}
                                        {assignment.job_quotes?.find(q => q.id === departmentId && q.quote_type === 'floor' && q.is_required) &&
                                            assignment.job_floors && assignment.job_floors.length > 0 && (
                                                <DropdownSection
                                                    title={<span className="inline-flex items-center gap-1.5">Floor Department{assignment.job_quotes?.find(q => q.id === departmentId && q.quote_type === 'floor' && q.is_required)?.on_hold && <span title="This quote is on hold" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-600 text-white leading-none whitespace-nowrap"><PauseCircle className="w-2.5 h-2.5" />ON HOLD</span>}</span>}
                                                    titleStyle="text-zinc-700 text-sm"
                                                    icon={HiOutlineSquare3Stack3D}
                                                    iconStyle="text-zinc-800"
                                                    subtext={``}
                                                    subtextStyle="text-xs "
                                                    defaultOpen={false}
                                                >
                                                    <div className="space-y-4">
                                                        {assignment.job_floors.map((floor) => (
                                                            <div key={floor.id} className="border-l-4 border-green-200 pl-4 space-y-2">
                                                                <p className="text-sm font-medium text-green-900">
                                                                    Floor {floor.floor_number}
                                                                </p>
                                                                <DetailRow
                                                                    label="Floor Truss Size"
                                                                    value={floor.floor_truss_size}
                                                                />
                                                                <DetailRow
                                                                    label="Flooring Type"
                                                                    value={floor.flooring}
                                                                />
                                                                <CheckboxDisplay
                                                                    label="Balcony Included"
                                                                    isChecked={floor.balcony_included}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </DropdownSection>
                                            )}

                                        {/* Site Measure */}
                                        {assignment.site_measure_required && (
                                            <InfoCard title="Site Measure Requirements" icon={FiMapPin} color="yellow">
                                                <div className="flex flex-wrap gap-3">
                                                    <CheckboxDisplay
                                                        label="Wall Measurement"
                                                        isChecked={assignment.site_measure_wall}
                                                    />
                                                    <CheckboxDisplay
                                                        label="Floor Measurement"
                                                        isChecked={assignment.site_measure_floor}
                                                    />
                                                    <CheckboxDisplay
                                                        label="Truss Measurement"
                                                        isChecked={assignment.site_measure_truss}
                                                    />
                                                </div>
                                            </InfoCard>
                                        )}
                                    </div>
                                )}
                            </>
                        </DropdownSection>
                    }


                    {/* JOB CHILDS */}
                    {/* {assignment?.selected_departments?.length > 0 && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-2">
                                <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider flex-1">Other Childs</h2>
                                <div className="text-sm text-gray-500 flex-shrink-0">
                                    {assignment.job_quotes?.filter(q => q.is_required).length} required
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                {assignment.job_quotes.filter(dep => dep?.is_required === true && dep?.id !== department?.id)
                                    .sort((a, b) => {
                                        const ORDER = { roof: 0, wall: 1, floor: 2, timber: 3 };
                                        const typeOrder = (ORDER[a.quote_type] ?? 99) - (ORDER[b.quote_type] ?? 99);
                                        if (typeOrder !== 0) return typeOrder;
                                        return (a.child_unique_id ?? '').localeCompare(b.child_unique_id ?? '');
                                    })
                                    .map(filteredDep => {
                                        const designers = assignment.job_designers?.filter(d =>
                                            filteredDep?.child_unique_id
                                                ? d.child_job_id === filteredDep.id
                                                : d.quote_type === filteredDep.quote_type
                                        ) || [];

                                        const typeColors = {
                                            roof: { bg: '', border: 'border-gray-200', icon: 'text-blue-600', iconBg: 'bg-blue-100' },
                                            wall: { bg: '', border: 'border-gray-200', icon: 'text-red-600', iconBg: 'bg-red-100' },
                                            floor: { bg: '', border: 'border-gray-200', icon: 'text-green-600', iconBg: 'bg-green-100' },
                                        };
                                        const c = typeColors[filteredDep.quote_type] || { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-600', iconBg: 'bg-gray-100' };

                                        return (
                                            <div
                                                key={filteredDep?.child_unique_id || filteredDep?.quote_type}
                                                onClick={() => navigate(`./${filteredDep?.id}`)}
                                                className={`group relative flex flex-col gap-1.5 rounded-lg border-2 ${c.border} ${c.bg} p-2 cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5`}
                                            >
                                                {filteredDep?.on_hold && (
                                                    <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-600 text-white leading-none">
                                                        ON HOLD
                                                    </span>
                                                )}

                                                {/* Header row */}
                    {/* <div className="flex items-center gap-1.5">
                                                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${c.iconBg} ${c.icon}`}>
                                                        {getQuoteIcon(filteredDep?.quote_type)}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="font-bold text-gray-900 text-xs leading-tight truncate">
                                                            {filteredDep?.child_unique_id}
                                                        </p>
                                                        <p className={`text-[10px] font-semibold capitalize ${c.icon} leading-none`}>
                                                            {filteredDep?.quote_type}
                                                        </p>
                                                    </div>
                                                    <FiArrowLeft className="w-3 h-3 text-gray-300 group-hover:text-gray-500 rotate-180 transition-colors flex-shrink-0" />
                                                </div> */}

                    {/* Status badges */}
                    {/* <div>{renderStatusFields(assignment, filteredDep)}</div> */}

                    {/* Footer */}
                    {/* <div className="flex items-center justify-between gap-1 pt-1 border-t border-gray-200/70">
                                                    {designers.length > 0 ? (
                                                        <div className="flex items-center gap-1 min-w-0">
                                                            <div className="flex -space-x-1">
                                                                {designers.slice(0, 2).map((d, i) => (
                                                                    <div key={i} className="w-4 h-4 rounded-full bg-gray-400 text-white text-[8px] font-bold flex items-center justify-center ring-1 ring-white flex-shrink-0">
                                                                        {d.name?.charAt(0)}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <span className="text-[10px] text-gray-500 truncate leading-none">
                                                                {designers.map(d => d.name?.split(' ')[0]).join(', ')}
                                                            </span>
                                                        </div> */}
                    {/* ) : (
                                                         <span className="text-[10px] text-gray-400 italic leading-none">No designer</span>
                                                     )} */}

                    {/* {filteredDep?.expected_finish_date || filteredDep?.expected_delivery_date && (
                                                        <div className="flex items-center gap-0.5 text-[10px] text-gray-500 flex-shrink-0">
                                                            <FiCalendar className="w-2.5 h-2.5" />
                                                            {formatDateDriver(filteredDep?.expected_finish_date || filteredDep?.expected_delivery_date)}
                                                        </div>
                                                    )} */}
                    {/* </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div> */}
                    {/* )}  */}


                    {/* Design Management Section */}
                    {(assignment.job_type === "Design" || assignment.job_type === "Design & Estimation") &&
                        hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) && (
                            <DropdownSection
                                title="Design Status & Actions"
                                titleStyle="text-zinc-800 text-base"
                                icon={FiDollarSign}
                                iconStyle="text-zinc-700 self-center"
                                subtext={`${assignment.job_quotes?.filter(q => q.is_required && q.id === department?.id).length || 0} sections required`}
                                subtextStyle="text-xs font-semibold text-zinc-600"
                                customClickableStyle="bg-zinc-200 hover:!bg-zinc-300 border-b-2 border-b-zinc-700"
                                defaultOpen={true}
                            >
                                <div className="grid gap-4">
                                    {assignment.job_quotes?.filter(q =>
                                        q.is_required &&
                                        q.delivery_count === null &&
                                        q?.id === department?.id
                                    ).map(quote => {
                                        if (quote.delivery_count ?? 0) return;
                                        const quoteType = quote.quote_type;
                                        const designers = assignment.job_designers?.filter(d => d.quote_type === quoteType && d.child_job_id === department.id);
                                        const userDesigner = designers?.find(d => d.user_id === userProfile.id);

                                        const canActAsDesigner =
                                            userProfile?.role === "designer" ||
                                            userProfile?.permissions?.includes("ASSIGN_DESIGNERS");


                                        const isUserAssigned = canActAsDesigner && userDesigner;

                                        const quoteDesigner = designers?.find(d => d.quote_type === quoteType && d.child_job_id === department.id);
                                        const isDesignCompleted = quoteDesigner?.is_completed === true;
                                        const isQuoteRequired = quote.is_required || false;

                                        if (!isQuoteRequired) return;

                                        return (
                                            <div key={quote?.child_unique_id || quoteType} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                                {/* Header */}
                                                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gray-100 text-gray-600">
                                                                {quoteType === 'roof' ? <FiHome className="w-4 h-4" /> :
                                                                    quoteType === 'wall' ? <FiLayers className="w-4 h-4" /> :
                                                                        <FiLayers className="w-4 h-4 rotate-90" />}
                                                            </div>
                                                            <div>
                                                                <h3 className="text-base font-semibold text-gray-900 capitalize">
                                                                    {quoteType} Design
                                                                </h3>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${quote.design_status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                                                                quote.design_status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                                                    'bg-amber-100 text-amber-800'
                                                                }`}>
                                                                {quote.design_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className="p-4 space-y-4">
                                                    {/* Assigned Designers */}
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <FiUser className="w-4 h-4 text-gray-400" />
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {designers?.length > 0 ? "Assigned Designers" : "No Designer Assigned"}
                                                            </span>
                                                        </div>

                                                        {designers && designers.length > 0 ? (
                                                            <div className="space-y-2">
                                                                {designers.map((designer) => (
                                                                    <div key={designer.id} className="flex flex-col space-y-3">
                                                                        <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                                                                            <div className="flex items-center gap-3">
                                                                                <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                                                                    {designer.name.charAt(0)}
                                                                                </div>
                                                                                <div>
                                                                                    <p className="font-medium text-gray-900">{designer.name}</p>
                                                                                    <p className="text-xs text-gray-500">
                                                                                        Assigned {formatDate(designer.assigned_at)}
                                                                                    </p>
                                                                                </div>

                                                                            </div>
                                                                            <div className="flex items-center gap-2">
                                                                                {designer.is_completed ? (
                                                                                    <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                                                                        <FiCheckCircle className="w-3 h-3" />
                                                                                        Completed
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                                                                        <FiClock className="w-3 h-3" />
                                                                                        In Progress
                                                                                    </div>
                                                                                )}
                                                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                                    <button
                                                                                        onClick={() => handleRemoveDesigner(quoteType, designer.user_id, designer.child_job_id || null)}
                                                                                        className="text-red-500 hover:text-red-700"
                                                                                    >
                                                                                        <FiX className="w-5 h-5" />
                                                                                    </button>
                                                                                )}
                                                                            </div>

                                                                        </div>
                                                                        <div className="flex flex-col">
                                                                            <div className="flex items-center gap-2 mb-3">
                                                                                <FiPenTool className="rotate-180 w-4 h-4 text-gray-400" />
                                                                                <p className="text-sm font-medium text-gray-700">Finishing Note</p>
                                                                            </div>

                                                                            <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm" >
                                                                                <p className={`text-sm ${designer?.finishing_note?.length <= 0 ? "text-gray-500 normal-case" : "text-green-700 italic"}`}>
                                                                                    {designer?.finishing_note || "Not specified any notes."}
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {designer?.is_completed &&
                                                                            <div className="flex flex-col">
                                                                                <div className="flex items-center gap-2 mb-3">
                                                                                    <FiCheckCircle className="w-4 h-4 text-green-400" />
                                                                                    <p className="text-sm font-medium text-green-700">Completed by {designer?.completed_by_name || designer?.name}</p>
                                                                                </div>
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center justify-center bg-white rounded-lg border-2 border-dashed border-gray-200">
                                                                {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                                                                    <button
                                                                        onClick={() => openAssignDesignerModal(quoteType)}
                                                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium w-full h-full py-4"
                                                                    >
                                                                        Assign Designer
                                                                    </button>
                                                                )}
                                                                {hasPermission(userProfile?.role, ["designer"]) && (
                                                                    <button
                                                                        onClick={() => handleSelfAssignDesigner(quoteType, department?.id, department?.child_unique_id)}
                                                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium w-full h-full py-4"
                                                                    >
                                                                        Assign Self
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Designer Action Panel - Only for assigned designers who haven't completed */}
                                                    {(isUserAssigned || hasPermission(userProfile?.role, ["super_admin", "admin"]))
                                                        && !isDesignCompleted && (
                                                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                                <div className="flex items-center gap-2 mb-4">
                                                                    <div className="w-5 h-5 bg-gray-700 rounded flex items-center justify-center">
                                                                        <FiTool className="w-3 h-3 text-white" />
                                                                    </div>
                                                                    <h4 className="font-semibold text-gray-800">Complete Your Design</h4>
                                                                </div>

                                                                <div className="space-y-4">
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                            Upload Design Files *
                                                                        </label>
                                                                        <div className="bg-white p-4 rounded-lg border-gray-200 hover:border-green-300 transition-colors">
                                                                            <DragDropUploader
                                                                                onFilesChange={handlePdfsChange}
                                                                                clearFiles={clearPdfs}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                            Completion Notes
                                                                        </label>
                                                                        <textarea
                                                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none text-sm placeholder-gray-400 bg-white"
                                                                            value={completionNotes[quoteType] || ''}
                                                                            onChange={(e) => setCompletionNotes(prev => ({
                                                                                ...prev,
                                                                                [quoteType]: e.target.value
                                                                            }))}
                                                                            rows="3"
                                                                            placeholder={`Add any notes about the completed ${quoteType} design work...`}
                                                                        />
                                                                    </div>

                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                            Delivery Note (Optional)
                                                                        </label>
                                                                        <textarea
                                                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm placeholder-gray-400 bg-white"
                                                                            value={quoteDeliveryNotes[quoteType] || ''}
                                                                            onChange={(e) => setQuoteDeliveryNotes(prev => ({
                                                                                ...prev,
                                                                                [quoteType]: e.target.value
                                                                            }))}
                                                                            rows="2"
                                                                            placeholder={`Add delivery instructions or notes for the ${quoteType} delivery...`}
                                                                        />
                                                                    </div>

                                                                    <div className="flex flex-col sm:flex-row sm:justify-end pt-2 gap-2">
                                                                        <button
                                                                            onClick={() => handleRemoveSelfAssignment("designer", quoteType)}
                                                                            className="px-6 py-3 bg-gradient-to-r from-red-700 to-red-800 text-white font-medium rounded-lg hover:from-red-800 hover:to-red-900 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        >
                                                                            <FiDelete className="w-4 h-4" />
                                                                            Unassign yourself
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleCompleteDesign(quoteType, designers)}
                                                                            disabled={isSubmittingCompletion || !uploadedFiles?.pdfFiles?.length}
                                                                            className="px-6 py-3 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-900 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        >
                                                                            {isSubmittingCompletion ? (
                                                                                <>
                                                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                                    Submitting...
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <FiCheckCircle className="w-4 h-4" />
                                                                                    Complete Design
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </DropdownSection>
                        )}

                    {/* ================= ESTIMATION MANAGEMENT SECTION ================= */}
                    {(assignment.job_type === "Estimation" || assignment.job_type === "Design & Estimation") &&
                        hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) && (() => {

                            // ================= HELPER VARIABLES =================
                            const estimator = assignment.job_estimators;

                            const isEstimatorAssigned = estimator?.user_id === userProfile.id;
                            const isAdmin = hasPermission(userProfile?.role, ["super_admin", "admin"]);

                            // Source of truth for completion status
                            const isEstimationCompleted =
                                estimator?.is_completed === true ||
                                assignment.job_quotes?.estimation_status === "completed";


                            const isAdminOrSuperAdmin = hasPermission(userProfile?.role, ["admin", "super_admin"]);

                            const isAssignedEstimator =
                                assignment.job_estimators?.user_id === userProfile?.id;

                            const estimatorExists = !!assignment.job_estimators;

                            const canCompleteEstimation =
                                !assignment.job_estimators?.is_completed &&
                                (
                                    isAssignedEstimator ||
                                    isAdminOrSuperAdmin
                                );


                            return (
                                <div className="space-y-4">
                                    <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 pb-2">
                                        Estimation Status & Actions
                                    </h2>

                                    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gray-100 text-gray-600">
                                                        <FiDollarSign className="w-4 h-4" />
                                                    </div>
                                                    <h3 className="text-base font-semibold text-gray-900">Estimation</h3>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-4 space-y-4">

                                            {/* ================= ASSIGNED ESTIMATOR ================= */}
                                            {estimator?.user_id ? (
                                                <div>
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <FiUser className="w-4 h-4 text-gray-400" />
                                                        <span className="text-sm font-medium text-gray-700">Assigned Estimator</span>
                                                    </div>
                                                    <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                                                {estimator.name?.charAt(0)}
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-gray-900">{estimator.name}</p>
                                                                <p className="text-xs text-gray-500">
                                                                    Assigned {formatDate(estimator.assigned_at)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {isEstimationCompleted ? (
                                                                <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                                                    <FiCheckCircle className="w-3 h-3" />
                                                                    Completed
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                                                    <FiClock className="w-3 h-3" />
                                                                    In Progress
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {isEstimationCompleted &&
                                                        <div className="flex flex-col mt-3">
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <FiCheckCircle className="w-4 h-4 text-green-400" />
                                                                <p className="text-sm font-medium text-green-700">Completed by {estimator?.completed_by_name || estimator?.name}</p>
                                                            </div>
                                                        </div>
                                                    }
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center bg-white rounded-lg border-2 border-dashed border-gray-200">
                                                    {isAdmin && (
                                                        <button
                                                            onClick={openAssignEstimatorModal}
                                                            className="text-xs text-purple-600 hover:text-purple-800 font-medium w-full h-full py-4"
                                                        >
                                                            Assign Estimator
                                                        </button>
                                                    )}
                                                    {hasPermission(userProfile?.role, ["estimator", "designer"]) && !isAdmin && (
                                                        <button
                                                            onClick={handleSelfAssignEstimator}
                                                            className="text-xs text-purple-600 hover:text-purple-800 font-medium w-full h-full py-4"
                                                        >
                                                            Assign Self
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Estimator Action Panel */}
                                            {canCompleteEstimation && (
                                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                    <div className="flex items-center gap-2 mb-4">
                                                        <div className="w-5 h-5 bg-gray-700 rounded flex items-center justify-center">
                                                            <FiDollarSign className="w-3 h-3 text-white" />
                                                        </div>
                                                        <h4 className="font-semibold text-gray-800">
                                                            Complete Estimation
                                                            {isAdminOrSuperAdmin && !isAssignedEstimator && (
                                                                <span className="ml-2 text-xs text-purple-600">(Admin Override)</span>
                                                            )}
                                                        </h4>
                                                    </div>

                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Upload Estimation Files *
                                                            </label>
                                                            <div className="bg-white p-4 rounded-lg border-gray-200 hover:border-purple-300 transition-colors">
                                                                <DragDropUploader
                                                                    onFilesChange={handlePdfsChange}
                                                                    clearFiles={clearPdfs}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Completion Notes
                                                            </label>
                                                            <textarea
                                                                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm placeholder-gray-400 bg-white"
                                                                value={completionNotes.estimation || ''}
                                                                onChange={(e) =>
                                                                    setCompletionNotes(prev => ({
                                                                        ...prev,
                                                                        estimation: e.target.value
                                                                    }))
                                                                }
                                                                rows="3"
                                                                placeholder="Add any notes about the completed estimation..."
                                                            />
                                                        </div>

                                                        <div className="flex flex-col sm:flex-row sm:justify-end pt-2 gap-2">
                                                            {/* Only assigned estimator can unassign themselves */}
                                                            {isAssignedEstimator && (
                                                                <button
                                                                    onClick={() => handleRemoveSelfAssignment("estimator", null)}
                                                                    className="px-6 py-3 bg-gradient-to-r from-red-700 to-red-800 text-white font-medium rounded-lg hover:from-red-800 hover:to-red-900 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                                                                >
                                                                    <FiDelete className="w-4 h-4" />
                                                                    Unassign yourself
                                                                </button>
                                                            )}

                                                            <button
                                                                onClick={() => handleCompleteEstimation(estimator)}
                                                                disabled={isSubmittingCompletion || !uploadedFiles?.pdfFiles?.length}
                                                                className="px-6 py-3 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-900 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {isSubmittingCompletion ? (
                                                                    <>
                                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                                        Submitting...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FiCheckCircle className="w-4 h-4" />
                                                                        Complete Estimation
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                </div>
                            );
                        })()}




                    {/* Designer Assignment Modal */}
                    {showDesignerModal && (
                        <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                            <div className="bg-white rounded-lg max-w-md w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-4 gap-2">
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900">
                                        Assign Designer for {selectedQuoteType?.charAt(0).toUpperCase() + selectedQuoteType?.slice(1)}{selectedChildQuoteName ? ` (${selectedChildQuoteName})` : ''}
                                    </h3>
                                    <button
                                        onClick={() => { setShowDesignerModal(false); setSelectedChildQuoteId(null); setSelectedChildQuoteName(null); }}
                                        className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                                    >
                                        <FiX className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                            Select Designer
                                        </label>
                                        <select
                                            value={selectedDesignerId}
                                            onChange={(e) => setSelectedDesignerId(e.target.value)}
                                            className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                                        >
                                            <option value="">Choose a designer...</option>
                                            {availableDesigners.map(designer => (
                                                <option key={designer.id} value={designer.id}>
                                                    {designer.username || designer.full_name} ({designer.role})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-3">
                                        <button
                                            onClick={() => handleAssignDesigner()}
                                            disabled={!selectedDesignerId}
                                            className="flex-1 py-3 md:py-2 px-4 bg-gray-800 text-white text-sm md:text-base rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                        >
                                            Assign Designer
                                        </button>
                                        <button
                                            onClick={() => { setShowDesignerModal(false); setSelectedChildQuoteId(null); setSelectedChildQuoteName(null); }}
                                            className="flex-1 py-3 md:py-2 px-4 bg-gray-300 text-gray-700 text-sm md:text-base rounded-lg hover:bg-gray-400 transition-colors min-h-[44px]"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Estimator Assignment Modal */}
                    {showEstimatorModal && (
                        <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                            <div className="bg-white rounded-lg max-w-md w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-4 gap-2">
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900">Assign Estimator</h3>
                                    <button
                                        onClick={() => setShowEstimatorModal(false)}
                                        className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                                    >
                                        <FiX className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                            Select Estimator
                                        </label>
                                        <select
                                            value={selectedEstimatorId}
                                            onChange={(e) => setSelectedEstimatorId(e.target.value)}
                                            className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                                        >
                                            <option value="">Choose an estimator...</option>
                                            {availableEstimators.map(estimator => (
                                                <option key={estimator.id} value={estimator.id}>
                                                    {estimator.username || estimator.full_name} ({estimator.role})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-3">
                                        <button
                                            onClick={handleAssignEstimator}
                                            disabled={!selectedEstimatorId}
                                            className="flex-1 py-3 md:py-2 px-4 bg-green-500 text-white text-sm md:text-base rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                        >
                                            Assign Estimator
                                        </button>
                                        <button
                                            onClick={() => setShowEstimatorModal(false)}
                                            className="flex-1 py-3 md:py-2 px-4 bg-gray-300 text-gray-700 text-sm md:text-base rounded-lg hover:bg-gray-400 transition-colors min-h-[44px]"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Driver Assignment Modal */}
                    {showDriverModal && (
                        <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                            <div className="bg-white rounded-lg max-w-md w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-4 gap-2">
                                    <h3 className="text-base md:text-lg font-semibold text-gray-900">
                                        Assign Driver for {selectedQuoteTypeForDriver?.charAt(0).toUpperCase() + selectedQuoteTypeForDriver?.slice(1)}{selectedChildQuoteNameForDriver ? ` (${selectedChildQuoteNameForDriver})` : ''} Delivery
                                    </h3>
                                    <button
                                        onClick={() => { setShowDriverModal(false); setSelectedQuoteTypeForDriver(null); setSelectedChildQuoteIdForDriver(null); setSelectedChildQuoteNameForDriver(null); }}
                                        className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                                    >
                                        <FiX className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                                            Select Driver
                                        </label>
                                        <select
                                            value={selectedDriverId}
                                            onChange={(e) => setSelectedDriverId(e.target.value)}
                                            className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                                        >
                                            <option value="">Choose a driver...</option>
                                            {availableDrivers.map(driver => (
                                                <option key={driver.id} value={`${driver.id}_${driver.username || driver.full_name}`}>
                                                    {driver.username || driver.full_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-3">
                                        <button
                                            onClick={handleAssignDriver}
                                            disabled={!selectedDriverId}
                                            className="flex-1 py-3 md:py-2 px-4 bg-teal-500 text-white text-sm md:text-base rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                                        >
                                            Assign Driver
                                        </button>
                                        <button
                                            onClick={() => { setShowDriverModal(false); setSelectedQuoteTypeForDriver(null); setSelectedDriverId(''); setSelectedChildQuoteIdForDriver(null); setSelectedChildQuoteNameForDriver(null); }}
                                            className="flex-1 py-3 md:py-2 px-4 bg-gray-300 text-gray-700 text-sm md:text-base rounded-lg hover:bg-gray-400 transition-colors min-h-[44px]"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!hasPermission(userProfile?.role, ["driver"]) && (
                        <ActivityLogsTable
                            logs={actionLogs}
                            showLogs={showLogs}
                            onToggle={() => setShowLogs(prev => !prev)}
                        />
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-gray-200">
                        {hasPermission(userProfile?.role, ["super_admin", "admin", "estimator", "designer"]) && (
                            <button
                                onClick={() => navigate(`/${companySlug}/assignment/${jobId}/edit`, {
                                    state: { from: location.pathname }
                                })}
                                className="flex-1 sm:flex-none px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium text-center"
                            >
                                Edit Assignment
                            </button>
                        )}
                        <button
                            onClick={() => navigate(`/${companySlug}/assignment/list`, {
                                state: { from: location.pathname }
                            })}
                            className="flex-1 sm:flex-none px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-center"
                        >
                            Back to List
                        </button>
                    </div>

                    {/* Delete Job Button - Only for Superadmin and Admin */}
                    {hasPermission(userProfile?.role, ['super_admin', 'admin']) && (
                        <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                    <h4 className="text-sm font-semibold text-red-800">Danger Zone</h4>
                                    <p className="text-xs text-red-600 mt-1">Permanently delete this job</p>
                                </div>
                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium w-full sm:w-auto"
                                >
                                    <FiTrash2 className="w-4 h-4" />
                                    Delete Job
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div >

            {/* File Viewer Modal */}
            {
                fileViewerOpen && selectedFile && (
                    <FileViewer
                        file={selectedFile}
                        filename={viewerFiles[selectedFileIndex].filename}
                        originalName={viewerFiles[selectedFileIndex].original_name}
                        fileType={viewerFiles[selectedFileIndex].file_type}
                        files={viewerFiles}
                        currentIndex={selectedFileIndex}
                        onNavigate={(newIndex) => {
                            setSelectedFileIndex(newIndex);
                            setSelectedFile(viewerFiles[newIndex]);
                        }}
                        onClose={() => setFileViewerOpen(false)}
                        jobId={assignment.id}
                        company={companySlug}
                    />
                )
            }

            {/* New Delivery Modal */}
            {showNewDeliveryModal && newDeliveryQuoteType && (
                <div className="mobile-dialog-shell fixed top-0 inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[80vh] overflow-hidden">
                        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-xl">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    Create New Delivery - {assignment.unique_id}
                                </h2>
                                <p className="text-sm text-gray-600 capitalize mt-1">
                                    {newDeliveryQuoteType} Quote
                                </p>
                            </div>
                            <button
                                onClick={handleCloseNewDeliveryModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6 overflow-y-auto flex-1">
                            {/* Delivery Status Info */}
                            {(() => {
                                const quote = assignment.job_quotes?.find(q => q.quote_type === newDeliveryQuoteType);
                                const deliveryStatus = quote?.delivery_status;

                                return (
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${deliveryStatus === 'delivered' ? 'bg-green-100 text-green-600' :
                                                deliveryStatus === 'in_transit' ? 'bg-blue-100 text-blue-600' :
                                                    'bg-gray-100 text-gray-400'
                                                }`}>
                                                <FiTruck className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">Current Status</p>
                                                <p className="text-sm text-gray-600 capitalize">
                                                    {deliveryStatus ? deliveryStatus.replace('_', ' ') : 'Not Scheduled'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Delivery Note */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Delivery Note
                                </label>
                                <textarea
                                    value={newDeliveryNote}
                                    onChange={(e) => setNewDeliveryNote(e.target.value)}
                                    placeholder="Enter delivery note..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    rows={4}
                                />
                            </div>

                            {/* Delivery Time Slot */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Delivery Time Slot
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {['1st', '2nd', '3rd', '4th', '5th'].map(slot => (
                                        <button
                                            key={slot}
                                            onClick={() => setNewDeliveryTimeSlot(slot)}
                                            className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${newDeliveryTimeSlot === slot
                                                ? 'border-gray-800 bg-gray-100 text-gray-800'
                                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                }`}
                                        >
                                            {slot}
                                        </button>
                                    ))}
                                    {newDeliveryTimeSlot && (
                                        <button
                                            onClick={() => setNewDeliveryTimeSlot(null)}
                                            className="px-4 py-3 rounded-lg border-2 border-red-200 hover:border-red-300 hover:bg-red-50 text-red-700 font-medium transition-all"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Scheduled For Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Scheduled For
                                </label>
                                <input
                                    type="date"
                                    value={newDeliveryScheduledDate}
                                    onChange={(e) => setNewDeliveryScheduledDate(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Unit Selection - Only for unit type assignments */}
                            {assignment.is_unit && assignment.unit_count > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Select Units ({newDeliverySelectedUnits.length} selected)
                                    </label>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                                        {Array.from({ length: assignment.unit_count }, (_, i) => i + 1).map(unitNumber => (
                                            <button
                                                key={unitNumber}
                                                onClick={() => handleToggleUnit(unitNumber)}
                                                className={`px-3 py-2 rounded-lg border-2 transition-all font-medium ${newDeliverySelectedUnits.includes(unitNumber)
                                                    ? 'border-gray-800 bg-gray-100 text-gray-800'
                                                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                    }`}
                                            >
                                                {unitNumber}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 flex-shrink-0 rounded-b-xl">
                            <button
                                onClick={handleCloseNewDeliveryModal}
                                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium w-full sm:w-auto text-center"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitNewDelivery}
                                className="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium w-full sm:w-auto text-center"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Job Confirmation Modal */}
            {showDeleteModal && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                    <FiTrash2 className="w-5 h-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Delete Job</h3>
                            </div>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteConfirmText('');
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <p className="text-sm text-red-800 font-medium">⚠️ This action is irreversible!</p>
                                <p className="text-xs text-red-600 mt-2">
                                    This will permanently delete the job and all associated data including notes, files, and delivery information.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-red-600">{assignment?.unique_id}</span> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                    placeholder="Enter job unique ID"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setDeleteConfirmText('');
                                    }}
                                    className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteJob}
                                    disabled={deleteConfirmText !== assignment?.unique_id || isDeletingJob}
                                    className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isDeletingJob ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <FiTrash2 className="w-4 h-4" />
                                            Delete Permanently
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <XeroInvoicePdfModal
                open={xeroPdfModal.open}
                onClose={closeXeroPdfModal}
                title={xeroPdfModal.title}
                pdfUrl={xeroPdfModal.pdfUrl}
                loading={xeroPdfModal.loading}
                error={xeroPdfModal.error}
            />
        </>
    );
};

export default SingleDepartment;